import { Component, OnInit, inject, signal, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import {
  CrmService,
  CasoDto,
  EmbudoDto,
  UsuarioDto,
  EtapaDto,
  CitaClinica,
  FichaContacto,
} from '../core/crm.service';
import { SesionService } from '../core/sesion.service';

type VistaModuloPendientes = 'inicio' | 'casos' | 'colas' | 'convenios' | 'biometria' | 'actividad';

@Component({
  selector: 'app-pendientes',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="cs-studio-page">
      @if (notificacionSistema()) {
        <div class="nexamed-toast-banner">
          <span>✓ {{ notificacionSistema() }}</span>
          <button type="button" class="nexamed-toast-close" (click)="notificacionSistema.set('')">Cerrar</button>
        </div>
      }
      <!-- =====================================================================
           VISTA 1: INICIO — RESUMEN EJECUTIVO NEXAMED (INDICADORES Y PROCESOS)
           ===================================================================== -->
      @if (vistaModulo() === 'inicio') {
        <header class="cs-header-card">
          <div class="csh-top-row">
            <div class="csh-title-zone">
              <h1 class="csh-record-title">
                NexaMed CRM · Resumen Ejecutivo — Centro Oftalmológico Ebenezer
              </h1>
            </div>
            <div class="csh-buttons-zone">
              <button type="button" class="cs-btn-blue" (click)="irAVista('casos')">
                Abrir Ficha de Casos ({{ casos().length }})
              </button>
              <button type="button" class="cs-btn-outlined" (click)="router.navigate(['/procesos'])">
                Escritorio Médico
              </button>
              <button type="button" class="cs-btn-green" (click)="abrirModalNuevoCaso()">
                + Nuevo Caso Clínico
              </button>
            </div>
          </div>
          <div class="csh-bottom-row">
            <div class="csh-tags-area">
              <span class="cs-mini-tag">Cumplimiento de tiempos: {{ resumen().cumplimientoPct }}%</span>
              <span class="cs-mini-tag">Quirófano 1: En turno (Dr. Javier Méndez)</span>
              <span class="cs-mini-tag">Pacientes en seguimiento: {{ casos().length }}</span>
            </div>
            <div class="csh-feed-links">
              <button type="button" class="csh-util-link" (click)="irAVista('colas')">
                <span>Control de Tiempos →</span>
              </button>
              <button type="button" class="csh-util-link" (click)="irAVista('biometria')">
                <span>Biometría & Citas →</span>
              </button>
            </div>
          </div>
        </header>

        <!-- 4 Bloques Ejecutivos Compactos -->
        <div class="cs-exec-kpis">
          <div class="cs-exec-kpi sky" (click)="irAVista('casos')">
            <div class="cek-lbl">Casos clínicos activos</div>
            <div class="cek-val">{{ casos().length }}</div>
            <div class="cek-sub">Clic para gestionar casos →</div>
          </div>
          <div class="cs-exec-kpi green" (click)="irAVista('colas')">
            <div class="cek-lbl">Atendidos a tiempo</div>
            <div class="cek-val">{{ resumen().cumplimientoPct }}%</div>
            <div class="cek-sub">{{ casosEnTermino().length }} casos dentro de meta</div>
          </div>
          <div class="cs-exec-kpi orange" (click)="irAVista('colas')">
            <div class="cek-lbl">Prioritarios / Por vencer</div>
            <div class="cek-val">{{ casosUrgentes().length }}</div>
            <div class="cek-sub">Requieren gestión hoy</div>
          </div>
          <div class="cs-exec-kpi royal" (click)="irAVista('biometria')">
            <div class="cek-lbl">Citas y cirugías hoy</div>
            <div class="cek-val">{{ citasInspectorMostradas().length }}</div>
            <div class="cek-sub">Sincronizadas con clínica</div>
          </div>
        </div>

        <div class="cs-two-col-body">
          <!-- Columna Izquierda en Inicio: Carga de Coordinadores y Quirófano -->
          <aside class="cs-left-col">
            <div class="cs-card cs-fields-card">
              <div class="cs-sec-title" style="margin-bottom: 4px;">Equipo de Coordinación</div>
              @for (u of usuarios(); track u.id) {
                <div class="cs-field-group">
                  <label class="cs-field-label">{{ u.rol | uppercase }}</label>
                  <div class="cs-underlined-val">{{ u.nombre }}</div>
                </div>
              }
              <div class="cs-done-status">
                <div class="cs-done-text">Meta operativa del día ({{ resumen().cumplimientoPct }}%)</div>
                <div class="cs-done-green-bar"></div>
              </div>
            </div>

            <div class="cs-consult-banner">
              <div class="cs-consult-text">
                Quirófano 1 activo · Facoemulsificación + Lente Trifocal OD
              </div>
              <button type="button" class="cs-consult-btn" (click)="router.navigate(['/procesos'], { queryParams: { vista: 'quirurgicos' } })">
                Ver programación quirúrgica
              </button>
            </div>
          </aside>

          <!-- Columna Derecha en Inicio: Resumen de Procesos y Casos Recientes -->
          <section class="cs-card cs-main-panel">
            <div class="cs-sec-block">
              <div class="cs-sec-header">
                <span class="cs-sq-chevron orange">⌃</span>
                <span class="cs-sec-title">Procesos clínicos activos en Centro Oftalmológico Ebenezer ({{ embudosConCasos().length }})</span>
              </div>
              <div class="cs-sec-content">
                <div class="cs-proc-cards-grid">
                  @for (e of embudosConCasos(); track e.id) {
                    <div class="cs-proc-summary-card" (click)="abrirProcesoEnCasos(e.id)">
                      <div class="cpsc-name">{{ e.nombre }}</div>
                      <div class="cpsc-count">{{ e.cantidad }} pacientes en flujo</div>
                      <span class="cpsc-link">Abrir casos →</span>
                    </div>
                  }
                </div>
              </div>
            </div>

            <div class="cs-sec-block">
              <div class="cs-sec-header">
                <span class="cs-sq-chevron orange">⌃</span>
                <span class="cs-sec-title">Pacientes prioritarios para atención inmediata</span>
              </div>
              <div class="cs-sec-content">
                <div class="cs-t-list">
                  @for (c of casos().slice(0, 6); track c.id) {
                    <div class="cs-t-row interactive" (click)="abrirCasoEnInspector(c)">
                      <span class="cs-t-icon">T</span>
                      <span class="cs-t-patient">{{ c.contacto }}</span>
                      <span class="cs-t-sep">—</span>
                      <span class="cs-t-proc">{{ c.titulo }}</span>
                      <span class="cs-t-meta">({{ c.etapa }} · {{ c.aseguradora || 'Póliza Sura' }})</span>
                      <div class="cs-t-actions">
                        <button type="button" class="cs-mini-step-btn" (click)="abrirCasoEnInspector(c); $event.stopPropagation()">
                          Abrir ficha →
                        </button>
                      </div>
                    </div>
                  }
                </div>
              </div>
            </div>
          </section>
        </div>
      }

      <!-- =====================================================================
           VISTA 2: CASOS CLÍNICOS — FICHA Y PARÁMETROS CREATIO 8 STUDIO
           ===================================================================== -->
      @else if (vistaModulo() === 'casos') {
        <!-- 1. ENCABEZADO EJECUTIVO DEL CASO ACTIVO -->
        <header class="cs-header-card casos-hero-header">
          <div class="csh-top-row">
            <div class="csh-title-zone">
              <button type="button" class="csh-back-arrow" title="Volver al Resumen Ejecutivo" (click)="irAVista('inicio')">
                ←
              </button>
              <div>
                <h1 class="csh-record-title">
                  {{ limpiarNombre(casoActualVista().contacto) }} · {{ casoActualVista().titulo }}
                </h1>
                <div class="casos-hero-badges">
                  <span class="casos-badge-sura">🛡️ {{ obtenerConvenioCaso(casoActualVista()) }}</span>
                  <span class="casos-badge-doc">{{ casoActualVista().contactoDocumento || 'CC 1000000015' }}</span>
                  <span class="casos-badge-proc">{{ casoActualVista().embudo }}</span>
                  @for (tag of (casoActualVista().etiquetas || []); track tag) {
                    <span class="cs-mini-tag removable" (click)="quitarEtiqueta(casoActualVista(), tag)" title="Quitar etiqueta">
                      {{ tag }} ×
                    </span>
                  }
                  <button type="button" class="csh-add-tag-link" (click)="rotarEtiquetaRapida(casoActualVista())">
                    + Etiqueta
                  </button>
                </div>
              </div>
            </div>

            <div class="csh-buttons-zone">
              <button type="button" class="cs-btn-outlined" (click)="abrirCotizacionPdf(casoActualVista())">
                📄 Cotización PDF
              </button>
              <button type="button" class="cs-btn-blue" (click)="guardarNotaRapida()">
                Guardar
              </button>
              <button type="button" class="cs-btn-outlined" (click)="abrirPaciente(casoActualVista().contactoId)">
                Expediente 360°
              </button>
              @if (siguienteEtapa(casoActualVista()); as sig) {
                <button type="button" class="cs-btn-green" (click)="avanzarEtapa(casoActualVista(), sig.id)">
                  Avanzar Etapa →
                </button>
              } @else {
                <button type="button" class="cs-btn-green" (click)="cambiarEstadoCaso(casoActualVista(), 'ganado')">
                  Completar Caso ✓
                </button>
              }
              <button type="button" class="cs-btn-dots" title="Nuevo caso clínico" (click)="abrirModalNuevoCaso()">
                +
              </button>
            </div>
          </div>
        </header>

        <!-- 2. CINTA HORIZONTAL DE ETAPAS TIPO CHEVRON (CREATIO 8 STUDIO) -->
        <div class="casos-chevron-ribbon">
          @for (et of etapasCasoActual(casoActualVista()).slice(0, 6); track et.id; let idx = $index) {
            <button
              type="button"
              class="casos-chevron-step"
              [class.done]="et.orden < ordenEtapaActual(casoActualVista())"
              [class.current]="et.id === casoActualVista().etapaId"
              (click)="avanzarEtapa(casoActualVista(), et.id)"
            >
              <span class="ccs-num">{{ idx + 1 }}.</span>
              <span class="ccs-name">{{ etapaLegible(et.nombre) }}</span>
            </button>
          }
        </div>

        <!-- 3. CUERPO EJECUTIVO EN 2 COLUMNAS -->
        <div class="casos-workspace-grid">
          <!-- Columna Izquierda: Tarjeta de Perfil Clínico del Paciente -->
          <aside class="casos-left-profile">
            <div class="cs-card casos-patient-card">
              <div class="cpc-avatar-wrap">
                <div class="cpc-avatar" [style.background]="colorAvatar(casoActualVista().contacto)">
                  {{ iniciales(casoActualVista().contacto) }}
                </div>
                <div class="cpc-name">{{ limpiarNombre(casoActualVista().contacto) }}</div>
                <div class="cpc-sub">{{ casoActualVista().titulo }}</div>
              </div>

              <div class="cpc-divider"></div>

              <div class="cpc-field">
                <label class="cpc-label">Cambiar paciente activo</label>
                <select
                  class="cs-underlined-select"
                  [ngModel]="casoActualVista().id"
                  (ngModelChange)="seleccionarCasoPorId($event)"
                >
                  @for (c of casos(); track c.id) {
                    <option [value]="c.id">{{ limpiarNombre(c.contacto) }} — {{ c.titulo }}</option>
                  }
                </select>
              </div>

              <div class="cpc-field">
                <label class="cpc-label">Médico / Responsable asignado</label>
                <select
                  class="cs-underlined-select"
                  [ngModel]="casoActualVista().responsableId || ''"
                  (ngModelChange)="asignarResponsableCaso(casoActualVista(), $event)"
                >
                  <option value="">Dra. Laura Gómez</option>
                  @for (u of usuarios(); track u.id) {
                    <option [value]="u.id">{{ u.nombre }}</option>
                  }
                </select>
              </div>

              <div class="cpc-field">
                <label class="cpc-label">Documento e Identificación</label>
                <div class="cpc-val">{{ casoActualVista().contactoDocumento || 'CC 1000000015' }} · {{ obtenerConvenioCaso(casoActualVista()) }}</div>
              </div>

              <div class="cpc-compliance-box">
                <div class="cpc-comp-head">
                  <span>Cumplimiento de tiempo</span>
                  <b>{{ resumen().cumplimientoPct }}% en tiempo</b>
                </div>
                <div class="cpc-comp-track">
                  <div class="cpc-comp-fill" [style.width.%]="resumen().cumplimientoPct"></div>
                </div>
              </div>

              <button type="button" class="cpc-wa-card" (click)="abrirConversacion(casoActualVista().id)">
                <span class="cpc-wa-icon">💬</span>
                <div class="cpc-wa-info">
                  <b>WhatsApp Directo</b>
                  <span>{{ casoActualVista().contactoTelefono || '+57 300 412 8899' }}</span>
                </div>
              </button>
            </div>

            <div class="cs-card cs-faq-card">
              <div class="cs-faq-title">
                <span class="cs-faq-q">✓</span>
                <span>Accesos clínicos rápidos</span>
              </div>
              <div class="cs-faq-links">
                <a (click)="irAVista('biometria')">Análisis biométrico OD / OI</a>
                <a (click)="irAVista('convenios')">Autorización de convenios y pólizas</a>
                <a (click)="irAVista('colas')">Control de tiempos y prioridades</a>
              </div>
            </div>
          </aside>

          <!-- Columna Derecha Principal: 3 Tarjetas de Resumen + Bandeja Ejecutiva -->
          <div class="casos-right-main">
            <!-- 3 Tarjetas Superiores de Resumen del Caso Seleccionado -->
            <div class="casos-top-summary-grid">
              <div class="cs-card csum-card" (click)="irAVista('convenios')">
                <div class="csum-label">Convenio y Radicado</div>
                <div class="csum-main-row">
                  <span class="csum-big">{{ obtenerConvenioCaso(casoActualVista()) }}</span>
                  <span class="csum-pill">{{ codigoRadicadoConvenio(casoActualVista(), 0) }}</span>
                </div>
                <div class="csum-sub">{{ tipoCoberturaConvenio(casoActualVista(), 0) }}</div>
              </div>

              <div class="cs-card csum-card" (click)="irAVista('biometria')">
                <div class="csum-label">Biometría OD / OI</div>
                <div class="csum-main-row">
                  <span class="csum-big">{{ biometriaActual().odMax }}D / LIO {{ biometriaActual().okMax }}D</span>
                  <span class="csum-pill blue">{{ etiquetaProcedimiento(casoActualVista()) }}</span>
                </div>
                <div class="csum-sub">OD K1 {{ biometriaActual().odMax }} · OI K1 {{ biometriaActual().oiMax }} · Cil -0.50D</div>
              </div>

              <div class="cs-card csum-card" (click)="irAVista('colas')">
                <div class="csum-label">Próxima Cita y Especialista</div>
                <div class="csum-main-row">
                  <span class="csum-big">Oftalmología</span>
                  <span class="csum-pill green">{{ textoCronometroTiempo(casoActualVista(), 0) }}</span>
                </div>
                <div class="csum-sub">{{ casoActualVista().responsable || 'Dra. Laura Gómez' }} · Etapa: {{ etapaLegible(casoActualVista().etapa) }}</div>
              </div>
            </div>

            <!-- Tarjeta Principal: Bandeja Ejecutiva de Casos Clínicos Activos -->
            <section class="cs-card casos-board-card">
              <div class="casos-board-header">
                <div>
                  <h2 class="casos-board-title">Bandeja Ejecutiva de Casos Clínicos Activos ({{ casosFiltrados().length }})</h2>
                  <p class="casos-board-sub">Selecciona cualquier paciente para actualizar su ficha superior o avanzar su etapa clínica</p>
                </div>

                <div class="casos-board-controls">
                  <button type="button" class="cs-btn-outlined" (click)="exportarCasosExcel()">
                    📤 Exportar Excel
                  </button>
                  <button type="button" class="cs-btn-outlined" (click)="fileInputExcel.click()">
                    📥 Importar Excel
                  </button>
                  <input
                    #fileInputExcel
                    type="file"
                    accept=".csv,.txt"
                    style="display: none;"
                    (change)="importarCasosDesdeExcel($event)"
                  />

                  <select class="cs-inline-select casos-proc-select" [ngModel]="embudoFiltro()" (ngModelChange)="cambiarProcesoFiltro($event)">
                    <option value="">Todos los procesos ({{ casos().length }})</option>
                    @for (e of embudosConCasos(); track e.id) {
                      <option [value]="e.id">{{ e.nombre }} ({{ e.cantidad }})</option>
                    }
                  </select>

                  <input
                    type="text"
                    class="tiempos-search-input"
                    placeholder="🔍 Buscar paciente, documento o cirugía..."
                    [ngModel]="filtroTexto()"
                    (ngModelChange)="filtroTexto.set($event)"
                  />
                </div>
              </div>

              <!-- Píldoras de Filtro Rápido -->
              <div class="casos-pills-bar">
                <button
                  type="button"
                  class="casos-filter-pill"
                  [class.active]="filtroRapido() === '' || filtroRapido() === 'hoy'"
                  (click)="filtroRapido.set('hoy'); embudoFiltro.set('')"
                >
                  Todos {{ casos().length }}
                </button>
                <button
                  type="button"
                  class="casos-filter-pill"
                  [class.active]="filtroRapido() === 'urgente'"
                  (click)="alternarFiltroRapido('urgente')"
                >
                  Prioritarios hoy ({{ casosUrgentes().length }})
                </button>
                <button
                  type="button"
                  class="casos-filter-pill"
                  [class.active]="filtroRapido() === 'cataratas'"
                  (click)="alternarFiltroRapido('cataratas')"
                >
                  Quirúrgicos
                </button>
                <button
                  type="button"
                  class="casos-filter-pill"
                  [class.active]="filtroRapido() === 'retina'"
                  (click)="alternarFiltroRapido('retina')"
                >
                  Diagnósticos y Exámenes
                </button>

                @if (embudoFiltro() || filtroTexto() || (filtroRapido() && filtroRapido() !== 'hoy')) {
                  <button type="button" class="t-filter-chip" (click)="limpiarFiltros()">
                    Limpiar filtros ×
                  </button>
                }
              </div>

              <!-- Tabla Ejecutiva Estructurada de Casos Clínicos -->
              <div class="tiempos-table-wrap">
                <table class="tiempos-table casos-exec-table">
                  <thead>
                    <tr>
                      <th>Paciente y Documento</th>
                      <th>Procedimiento Clínico</th>
                      <th>Proceso y Etapa</th>
                      <th>Convenio</th>
                      <th>Estado de Tiempo</th>
                      <th style="text-align: right;">Acción Rápida</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (c of casosFiltrados().slice(0, 14); track c.id; let idx = $index) {
                      <tr
                        class="casos-exec-row"
                        [class.selected-case-row]="casoActualVista().id === c.id"
                        (click)="seleccionarCasoInspector(c)"
                      >
                        <td>
                          <div class="tt-pat-cell">
                            <div class="tt-avatar" [style.background]="colorAvatar(c.contacto)">
                              {{ iniciales(c.contacto) }}
                            </div>
                            <div>
                              <div class="tt-pat-name">{{ limpiarNombre(c.contacto) }}</div>
                              <div class="tt-pat-sub">{{ c.contactoDocumento || 'CC 1000000015' }}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div class="tt-proc-title">{{ c.titulo }}</div>
                          <div class="tt-pat-sub">{{ c.embudo }}</div>
                        </td>
                        <td>
                          <span
                            class="casos-stage-pill"
                            [attr.data-tone]="idx % 4 === 0 ? 'coral' : (idx % 4 === 1 ? 'blue' : (idx % 4 === 2 ? 'green' : 'amber'))"
                          >
                            {{ etapaLegible(c.etapa) }}
                          </span>
                        </td>
                        <td>
                          <span class="conv-entity-pill" [attr.data-ent]="entidadConvenioDistribuida(c, idx)">
                            {{ obtenerConvenioCaso(c) }}
                          </span>
                        </td>
                        <td>
                          <span class="tt-timer-pill" [class.crit]="esUrgente(c)" [class.ok]="!esUrgente(c)">
                            {{ textoCronometroTiempo(c, idx) }}
                          </span>
                        </td>
                        <td style="text-align: right;" (click)="$event.stopPropagation()">
                          @if (siguienteEtapaPositiva(c); as sig) {
                            <button type="button" class="casos-advance-link" (click)="avanzarEtapa(c, sig.id)">
                              Avanzar etapa →
                            </button>
                          } @else {
                            <button type="button" class="casos-advance-link done" (click)="cambiarEstadoCaso(c, 'ganado')">
                              ✓ Completar
                            </button>
                          }
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>

              <!-- Barra Inferior de Registro Rápido de Nota Clínica / WhatsApp -->
              <div class="casos-quick-note-bar">
                <input
                  type="text"
                  class="cs-clean-input"
                  placeholder="Escribir nota clínica de evolución o mensaje rápido para {{ limpiarNombre(casoActualVista().contacto) }}..."
                  [(ngModel)]="textoRespuestaRapida"
                  (keyup.enter)="enviarRespuestaRapida()"
                />
                <button type="button" class="cs-btn-blue" (click)="enviarRespuestaRapida()">
                  Guardar nota clínica
                </button>
              </div>
              @if (mensajesEnviadosInspector().length > 0) {
                <div class="casos-notes-saved-list">
                  @for (m of mensajesEnviadosInspector(); track m) {
                    <div class="casos-note-chip">✓ {{ m }}</div>
                  }
                </div>
              }
            </section>
          </div>
        </div>
      }

      <!-- =====================================================================
           VISTA 3: CONTROL DE TIEMPOS Y TURNOS (ESTILO NEXAMED CRM STUDIO)
           ===================================================================== -->
      @else if (vistaModulo() === 'colas') {
        <div class="tiempos-title-bar">
          <div style="display: flex; align-items: center; gap: 10px;">
            <button type="button" class="csh-back-arrow" (click)="irAVista('inicio')">←</button>
            <h1 class="tiempos-main-h1">Control de Tiempos y Prioridades de Atención</h1>
          </div>
          <div style="display: flex; align-items: center; gap: 8px;">
            <button
              type="button"
              class="cs-btn-outlined"
              [class.active-filter]="estadoFiltro() === 'abierto'"
              (click)="cambiarEstadoFiltro('abierto')"
            >
              En seguimiento ({{ casos().length }})
            </button>
            <button
              type="button"
              class="cs-btn-outlined"
              [class.active-filter]="estadoFiltro() === 'ganado'"
              (click)="cambiarEstadoFiltro('ganado')"
            >
              Completados
            </button>
            <button type="button" class="cs-btn-blue" (click)="irAVista('casos')">
              Ir a Ficha de Caso
            </button>
          </div>
        </div>

        <!-- 1. FILA DE 4 TARJETAS DE TELEMETRÍA CON ANILLOS SVG Y MICRO-BARRAS -->
        <div class="tiempos-kpi-grid">
          <!-- Tarjeta 1: Nivel de Servicio Global -->
          <div class="tkpi-card" (click)="filtroPestanaTiempos.set('todos')">
            <div class="tkpi-top">
              <div class="tkpi-ring-wrap">
                <svg class="tkpi-svg" viewBox="0 0 36 36">
                  <path class="tkpi-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                  <path class="tkpi-stroke navy" [attr.stroke-dasharray]="resumen().cumplimientoPct + ', 100'" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                </svg>
                <span class="tkpi-ring-txt">{{ resumen().cumplimientoPct }}%</span>
              </div>
              <div class="tkpi-info">
                <div class="tkpi-big">{{ resumen().cumplimientoPct }}%</div>
                <div class="tkpi-desc">Cumplimiento de<br>Tiempos</div>
                <div class="tkpi-bars blue">
                  <span style="height: 55%"></span><span style="height: 80%"></span><span style="height: 60%"></span>
                  <span style="height: 92%"></span><span style="height: 75%"></span><span style="height: 68%"></span>
                  <span style="height: 96%"></span><span style="height: 85%"></span>
                </div>
              </div>
            </div>
            <div class="tkpi-foot">
              <b>Cumplimiento Global de Atención</b>
              <span>(Meta clínica 90%)</span>
            </div>
          </div>

          <!-- Tarjeta 2: Prioritarios < 2h -->
          <div class="tkpi-card" (click)="filtroPestanaTiempos.set('criticos')">
            <div class="tkpi-top">
              <div class="tkpi-ring-wrap">
                <svg class="tkpi-svg" viewBox="0 0 36 36">
                  <path class="tkpi-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                  <path class="tkpi-stroke coral" stroke-dasharray="68, 100" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                </svg>
                <span class="tkpi-ring-txt">{{ casosCriticosDosHoras().length }}</span>
              </div>
              <div class="tkpi-info">
                <div class="tkpi-big coral-txt">{{ casosCriticosDosHoras().length }}</div>
                <div class="tkpi-desc">Prioritarios en<br>Ventana &lt; 2h</div>
                <div class="tkpi-bars coral">
                  <span style="height: 35%"></span><span style="height: 92%"></span><span style="height: 45%"></span>
                  <span style="height: 75%"></span><span style="height: 60%"></span><span style="height: 30%"></span>
                  <span style="height: 55%"></span><span style="height: 40%"></span>
                </div>
              </div>
            </div>
            <div class="tkpi-foot">
              <b>{{ casosCriticosDosHoras().length }} Prioritarios en Ventana &lt; 2h</b>
              <span>(Atención inmediata)</span>
            </div>
          </div>

          <!-- Tarjeta 3: Tiempo Medio de Respuesta -->
          <div class="tkpi-card" (click)="filtroPestanaTiempos.set('termino')">
            <div class="tkpi-top">
              <div class="tkpi-ring-wrap">
                <svg class="tkpi-svg" viewBox="0 0 36 36">
                  <path class="tkpi-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                  <path class="tkpi-stroke green" stroke-dasharray="84, 100" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                </svg>
                <span class="tkpi-ring-txt">18 min</span>
              </div>
              <div class="tkpi-info">
                <div class="tkpi-big">18 min</div>
                <div class="tkpi-desc">Tiempo Medio<br>de Respuesta</div>
                <div class="tkpi-bars green">
                  <span style="height: 45%"></span><span style="height: 70%"></span><span style="height: 95%"></span>
                  <span style="height: 65%"></span><span style="height: 50%"></span><span style="height: 78%"></span>
                  <span style="height: 60%"></span><span style="height: 52%"></span>
                </div>
              </div>
            </div>
            <div class="tkpi-foot">
              <b>Tiempo Medio de Respuesta</b>
              <span>(Ritmo óptimo)</span>
            </div>
          </div>

          <!-- Tarjeta 4: Coordinadores en Turno -->
          <div class="tkpi-card" (click)="responsableTiemposFiltro.set('')">
            <div class="tkpi-top">
              <div class="tkpi-ring-wrap">
                <svg class="tkpi-svg" viewBox="0 0 36 36">
                  <path class="tkpi-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                  <path class="tkpi-stroke green" stroke-dasharray="88, 100" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                </svg>
                <span class="tkpi-ring-txt">{{ coordinadoresTurno().length }}</span>
              </div>
              <div class="tkpi-info">
                <div class="tkpi-big">{{ coordinadoresTurno().length }}</div>
                <div class="tkpi-desc">Coordinadores<br>en Turno</div>
                <div class="tkpi-bars green">
                  <span style="height: 50%"></span><span style="height: 65%"></span><span style="height: 88%"></span>
                  <span style="height: 72%"></span><span style="height: 60%"></span><span style="height: 92%"></span>
                  <span style="height: 55%"></span><span style="height: 62%"></span>
                </div>
              </div>
            </div>
            <div class="tkpi-foot">
              <b>{{ coordinadoresTurno().length }} Coordinadores en Turno</b>
              <span>(Carga balanceada)</span>
            </div>
          </div>
        </div>

        <!-- 2. PANEL INTERMEDIO DIVIDIDO: RENDIMIENTO POR PROCESO + TURNOS EN VIVO -->
        <div class="tiempos-mid-grid">
          <section class="cs-card tiempos-mid-card">
            <div class="tmid-title">Rendimiento de Tiempos por Proceso Clínico</div>
            <div class="tmid-bars-list">
              @for (item of rendimientoPorProceso(); track item.nombre) {
                <div
                  class="tmid-bar-row"
                  [class.selected]="procesoTiemposFiltro() === item.nombre"
                  (click)="alternarProcesoTiempos(item.nombre)"
                  title="Clic para filtrar este proceso"
                >
                  <span class="tmid-proc-lbl">{{ item.nombre }} ({{ item.total }})</span>
                  <div class="tmid-bar-track">
                    <div class="tmid-bar-fill" [style.width.%]="item.porcentaje"></div>
                  </div>
                  <span class="tmid-pct-val">{{ item.porcentaje }}%</span>
                </div>
              }
            </div>
          </section>

          <section class="cs-card tiempos-mid-card">
            <div class="tmid-title">Carga Operativa y Turnos en Vivo</div>
            <div class="tmid-coord-list">
              @for (coord of coordinadoresTurno(); track coord.nombre) {
                <div
                  class="tmid-coord-row"
                  [class.selected]="responsableTiemposFiltro() === coord.nombre"
                  (click)="alternarResponsableTiempos(coord.nombre)"
                  title="Clic para ver los pacientes asignados a {{ coord.nombre }}"
                >
                  <div class="tmid-avatar">{{ iniciales(coord.nombre) }}</div>
                  <div class="tmid-coord-text">
                    <b>{{ coord.nombre }}</b> · {{ coord.casos }} casos · <span>{{ coord.cumplimiento }}% en tiempo</span>
                  </div>
                </div>
              }
            </div>
          </section>
        </div>

        <!-- 3. TABLA EJECUTIVA DE CONTROL DE TIEMPOS Y TURNOS -->
        <section class="cs-card tiempos-table-card">
          <div class="ttab-header">
            <div>
              <div class="ttab-title">Matriz Ejecutiva de Control de Tiempos y Turnos</div>
              <div class="ttab-tabs">
                <button
                  type="button"
                  class="ttab-btn"
                  [class.active]="filtroPestanaTiempos() === 'todos'"
                  (click)="filtroPestanaTiempos.set('todos')"
                >
                  Todos ({{ casos().length }})
                </button>
                <button
                  type="button"
                  class="ttab-btn"
                  [class.active]="filtroPestanaTiempos() === 'criticos'"
                  (click)="filtroPestanaTiempos.set('criticos')"
                >
                  Prioridad Alta ({{ casosCriticosDosHoras().length }})
                </button>
                <button
                  type="button"
                  class="ttab-btn"
                  [class.active]="filtroPestanaTiempos() === 'hoy'"
                  (click)="filtroPestanaTiempos.set('hoy')"
                >
                  Por Vencer Hoy ({{ casosUrgentes().length }})
                </button>
                <button
                  type="button"
                  class="ttab-btn"
                  [class.active]="filtroPestanaTiempos() === 'termino'"
                  (click)="filtroPestanaTiempos.set('termino')"
                >
                  En Término ({{ casosEnTermino().length }})
                </button>
              </div>
            </div>

            <div class="ttab-filters">
              @if (procesoTiemposFiltro() || responsableTiemposFiltro() || busquedaTiempos()) {
                <button type="button" class="ttab-clear" (click)="limpiarFiltrosTiempos()">
                  ✕ Limpiar filtro
                </button>
              }
              <input
                type="search"
                class="ttab-search"
                placeholder="Buscar paciente o procedimiento..."
                [ngModel]="busquedaTiempos()"
                (ngModelChange)="busquedaTiempos.set($event)"
              />
            </div>
          </div>

          <div class="ttab-scroll">
            <table class="ttab-table">
              <thead>
                <tr>
                  <th>Tiempo Restante ↓</th>
                  <th>Paciente</th>
                  <th>Procedimiento y Proceso</th>
                  <th>Convenio</th>
                  <th>Estado de Tiempo</th>
                  <th>Responsable</th>
                  <th style="text-align: right;">Acción</th>
                </tr>
              </thead>
              <tbody>
                @for (c of casosTiemposFiltrados(); track c.id; let idx = $index) {
                  <tr (click)="abrirCasoEnInspector(c)">
                    <td>
                      <span class="ttab-timer" [class.urgent]="esCriticoDosHoras(c, idx)">
                        {{ textoCronometroTiempo(c, idx) }}
                      </span>
                    </td>
                    <td>
                      <div class="ttab-patient-cell">
                        <div class="ttab-avatar">{{ iniciales(limpiarNombre(c.contacto)) }}</div>
                        <div>
                          <div class="ttab-pname">{{ limpiarNombre(c.contacto) }}</div>
                          <div class="ttab-pdoc">{{ c.contactoDocumento || 'CC 45.123.987' }}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div class="ttab-proc">{{ c.titulo }}</div>
                      <div class="ttab-subproc">{{ c.embudo }} · Etapa: {{ etapaLegible(c.etapa) }}</div>
                    </td>
                    <td>
                      <span class="ttab-conv">{{ obtenerConvenioCaso(c) }}</span>
                    </td>
                    <td>
                      <div class="ttab-health-bar">
                        <span class="thb-green" [style.flex]="esCriticoDosHoras(c, idx) ? 1.4 : 2.2"></span>
                        <span class="thb-amber" [style.flex]="esCriticoDosHoras(c, idx) ? 1.1 : 0.7"></span>
                        <span class="thb-rest"></span>
                      </div>
                    </td>
                    <td (click)="$event.stopPropagation()">
                      <select
                        class="ttab-resp-select"
                        [ngModel]="c.responsableId || ''"
                        (ngModelChange)="asignarResponsableCaso(c, $event)"
                      >
                        <option value="">{{ c.responsable || 'Dra. Laura Gómez' }}</option>
                        @for (u of usuarios(); track u.id) {
                          <option [value]="u.id">{{ u.nombre }}</option>
                        }
                      </select>
                    </td>
                    <td style="text-align: right;" (click)="$event.stopPropagation()">
                      <div style="display: inline-flex; gap: 6px; align-items: center;">
                        @if (siguienteEtapaPositiva(c); as sig) {
                          <button
                            type="button"
                            class="ttab-step-btn"
                            title="Avanzar a {{ sig.nombre }}"
                            (click)="avanzarEtapa(c, sig.id)"
                          >
                            → {{ acortarEtapa(sig.nombre) }}
                          </button>
                        }
                        <button
                          type="button"
                          class="ttab-manage-btn"
                          (click)="abrirCasoEnInspector(c)"
                        >
                          Gestionar turno →
                        </button>
                      </div>
                    </td>
                  </tr>
                } @empty {
                  <tr>
                    <td colspan="7" style="padding: 28px; text-align: center; color: #64748B;">
                      No hay pacientes en esta vista con los filtros actuales.
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </section>
      }

      <!-- =====================================================================
           VISTA 4: CONVENIOS, PÓLIZAS DE SALUD Y AUTORIZACIONES QUIRÚRGICAS
           ===================================================================== -->
      @else if (vistaModulo() === 'convenios') {
        <div class="tiempos-title-bar">
          <div style="display: flex; align-items: center; gap: 10px;">
            <button type="button" class="csh-back-arrow" (click)="irAVista('inicio')">←</button>
            <h1 class="tiempos-main-h1">Entidad Aseguradora · Convenios, Pólizas y Autorizaciones</h1>
          </div>
          <div style="display: flex; align-items: center; gap: 8px;">
            <button
              type="button"
              class="cs-btn-outlined"
              [class.active-filter]="filtroConvenio() === '' && estadoAutorizacionFiltro() === 'todas'"
              (click)="limpiarFiltrosConvenios()"
            >
              Todas ({{ casos().length }})
            </button>
            <button type="button" class="cs-btn-blue" (click)="abrirModalNuevoCaso()">
              + Radicar nueva orden
            </button>
          </div>
        </div>

        <!-- 1. FILA SUPERIOR DE 4 TARJETAS POR ENTIDAD ASEGURADORA -->
        <div class="conv-cards-grid">
          @for (ent of resumenEntidadesConvenio(); track ent.clave) {
            <article
              class="conv-entity-card"
              [class.selected]="filtroConvenio() === ent.clave"
              (click)="alternarEntidadConvenio(ent.clave)"
            >
              <div class="cec-top">
                <div class="cec-badge-pill" [attr.data-tone]="ent.tono">
                  <span class="cec-ico">{{ ent.icono }}</span>
                  <span class="cec-dot"></span>
                  <span>{{ ent.pendientes }} en trámite</span>
                </div>
                <span class="cec-dots">⋮</span>
              </div>

              <div class="cec-body">
                <div class="cec-title">{{ ent.titulo }}</div>
                <div class="cec-sub">{{ ent.total }} órdenes · {{ ent.aprobacionPct }}% aprobadas</div>
              </div>

              <div class="cec-bar-track">
                <div
                  class="cec-bar-fill"
                  [attr.data-tone]="ent.tono"
                  [style.width.%]="ent.aprobacionPct"
                ></div>
              </div>
            </article>
          }
        </div>

        <!-- 2. PANEL INTERMEDIO DIVIDIDO: TASA DE APROBACIÓN + RESUMEN DE RADICADOS -->
        <div class="tiempos-mid-grid">
          <section class="cs-card tiempos-mid-card">
            <div class="tmid-title">Tasa de Aprobación y Cobertura por Aseguradora</div>
            <div class="conv-chart-rows">
              @for (ent of resumenEntidadesConvenio(); track ent.clave) {
                <div class="conv-chart-row" (click)="alternarEntidadConvenio(ent.clave)">
                  <span class="ccr-lbl">{{ ent.nombreCorto }}</span>
                  <div class="ccr-bars">
                    <div class="ccr-bar-line">
                      <span class="ccr-fill-main" [attr.data-tone]="ent.tono" [style.width.%]="ent.aprobacionPct"></span>
                    </div>
                    <div class="ccr-bar-line">
                      <span class="ccr-fill-sub" [style.width.%]="ent.coberturaPct"></span>
                    </div>
                  </div>
                </div>
              }
              <div class="conv-chart-axis">
                <span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span>
              </div>
            </div>
          </section>

          <section class="cs-card tiempos-mid-card">
            <div class="tmid-title">Resumen de Radicados y Estados de Orden</div>
            <div class="conv-summary-metrics">
              <div
                class="csm-item"
                [class.selected]="estadoAutorizacionFiltro() === 'autorizada'"
                (click)="estadoAutorizacionFiltro.set(estadoAutorizacionFiltro() === 'autorizada' ? 'todas' : 'autorizada')"
              >
                <div class="csm-val green">{{ conteoEstadosConvenio().autorizadas }}</div>
                <div class="csm-lbl">Autorizadas listas para Qx</div>
              </div>

              <div
                class="csm-item"
                [class.selected]="estadoAutorizacionFiltro() === 'tramite'"
                (click)="estadoAutorizacionFiltro.set(estadoAutorizacionFiltro() === 'tramite' ? 'todas' : 'tramite')"
              >
                <div class="csm-val bronze">{{ conteoEstadosConvenio().enTramite }}</div>
                <div class="csm-lbl">En validación médica</div>
              </div>

              <div
                class="csm-item"
                [class.selected]="estadoAutorizacionFiltro() === 'pendiente'"
                (click)="estadoAutorizacionFiltro.set(estadoAutorizacionFiltro() === 'pendiente' ? 'todas' : 'pendiente')"
              >
                <div class="csm-val brown">{{ conteoEstadosConvenio().pendientes }}</div>
                <div class="csm-lbl">Pendientes de soporte</div>
              </div>
            </div>

            <div class="conv-summary-footer">
              <div>
                <span class="csf-big">$124,8M</span>
                <span class="csf-txt">Monto quirúrgico y diagnóstico cubierto en curso</span>
              </div>
              <span class="csf-badge">Facturación electrónica activa</span>
            </div>
          </section>
        </div>

        <!-- 3. TABLA EJECUTIVA DE AUTORIZACIONES Y PÓLIZAS -->
        <section class="cs-card tiempos-table-card">
          <div class="ttab-header">
            <div>
              <div class="ttab-title">Tabla Ejecutiva de Autorizaciones y Pólizas</div>
              <div class="ttab-tabs">
                <button
                  type="button"
                  class="ttab-btn"
                  [class.active]="estadoAutorizacionFiltro() === 'todas' && filtroConvenio() === ''"
                  (click)="limpiarFiltrosConvenios()"
                >
                  Todas ({{ casos().length }})
                </button>
                <button
                  type="button"
                  class="ttab-btn"
                  [class.active]="estadoAutorizacionFiltro() === 'autorizada'"
                  (click)="estadoAutorizacionFiltro.set('autorizada')"
                >
                  Autorizadas ({{ conteoEstadosConvenio().autorizadas }})
                </button>
                <button
                  type="button"
                  class="ttab-btn"
                  [class.active]="estadoAutorizacionFiltro() === 'tramite'"
                  (click)="estadoAutorizacionFiltro.set('tramite')"
                >
                  En Trámite ({{ conteoEstadosConvenio().enTramite }})
                </button>
                <button
                  type="button"
                  class="ttab-btn"
                  [class.active]="estadoAutorizacionFiltro() === 'pendiente'"
                  (click)="estadoAutorizacionFiltro.set('pendiente')"
                >
                  Pendientes ({{ conteoEstadosConvenio().pendientes }})
                </button>
                <button
                  type="button"
                  class="ttab-btn"
                  [class.active]="filtroConvenio() === 'Póliza Sura'"
                  (click)="alternarEntidadConvenio('Póliza Sura')"
                >
                  Póliza Sura
                </button>
                <button
                  type="button"
                  class="ttab-btn"
                  [class.active]="filtroConvenio() === 'Sanitas EPS'"
                  (click)="alternarEntidadConvenio('Sanitas EPS')"
                >
                  Sanitas EPS
                </button>
                <button
                  type="button"
                  class="ttab-btn"
                  [class.active]="filtroConvenio() === 'Mutual'"
                  (click)="alternarEntidadConvenio('Mutual')"
                >
                  Mutual
                </button>
                <button
                  type="button"
                  class="ttab-btn"
                  [class.active]="filtroConvenio() === 'Particular'"
                  (click)="alternarEntidadConvenio('Particular')"
                >
                  Particulares
                </button>
              </div>
            </div>

            <div class="ttab-filters">
              @if (filtroConvenio() || estadoAutorizacionFiltro() !== 'todas' || busquedaConvenios()) {
                <button type="button" class="ttab-clear" (click)="limpiarFiltrosConvenios()">
                  ✕ Limpiar filtro
                </button>
              }
              <input
                type="search"
                class="ttab-search"
                placeholder="Buscar radicado, paciente o documento..."
                [ngModel]="busquedaConvenios()"
                (ngModelChange)="busquedaConvenios.set($event)"
              />
            </div>
          </div>

          <div class="ttab-scroll">
            <table class="ttab-table">
              <thead>
                <tr>
                  <th>N° Radicado</th>
                  <th>Paciente y Documento</th>
                  <th>Entidad / Convenio</th>
                  <th>Procedimiento</th>
                  <th>Cobertura</th>
                  <th>Estado</th>
                  <th style="text-align: right;">Acción</th>
                </tr>
              </thead>
              <tbody>
                @for (c of casosPorConvenio(); track c.id; let idx = $index) {
                  <tr (click)="abrirCasoEnInspector(c)">
                    <td>
                      <span class="conv-radicado">{{ codigoRadicadoConvenio(c, idx) }}</span>
                    </td>
                    <td>
                      <span class="ttab-pname">{{ limpiarNombre(c.contacto) }}</span>
                      <span class="conv-doc-inline"> · {{ c.contactoDocumento || 'CC 45123987' }}</span>
                    </td>
                    <td>
                      <span class="conv-entity-pill" [attr.data-ent]="entidadConvenioDistribuida(c, idx)">
                        {{ entidadConvenioDistribuida(c, idx) }}
                      </span>
                    </td>
                    <td>
                      <span class="ttab-proc">{{ c.titulo }}</span>
                    </td>
                    <td>
                      <span class="conv-cov-txt">{{ tipoCoberturaConvenio(c, idx) }}</span>
                    </td>
                    <td>
                      @if (estadoOrdenConvenio(c, idx) === 'autorizada') {
                        <span class="conv-status-ok">✓ Autorizada</span>
                      } @else if (estadoOrdenConvenio(c, idx) === 'tramite') {
                        <span class="conv-status-wait">! En trámite</span>
                      } @else {
                        <span class="conv-status-pend">• Pendiente soporte</span>
                      }
                    </td>
                    <td style="text-align: right;" (click)="$event.stopPropagation()">
                      <button
                        type="button"
                        class="ttab-step-btn"
                        style="margin-right: 6px;"
                        (click)="abrirCotizacionPdf(c)"
                      >
                        📄 Orden PDF
                      </button>
                      @if (estadoOrdenConvenio(c, idx) === 'autorizada') {
                        <button
                          type="button"
                          class="ttab-step-btn"
                          (click)="abrirCasoEnInspector(c)"
                        >
                          Ver caso →
                        </button>
                      } @else {
                        <button
                          type="button"
                          class="ttab-manage-btn"
                          (click)="autorizarOrdenConvenio(c)"
                        >
                          Autorizar orden →
                        </button>
                      }
                    </td>
                  </tr>
                } @empty {
                  <tr>
                    <td colspan="7" style="padding: 28px; text-align: center; color: #64748B;">
                      No hay órdenes registradas con los filtros seleccionados.
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </section>
      }

      <!-- =====================================================================
           VISTA 5: BIOMETRÍA & CITAS — CALENDARIO VISUAL DE AGENDA Y QUIRÓFANOS
           ===================================================================== -->
      @else if (vistaModulo() === 'biometria') {
        <div class="tiempos-title-bar">
          <div style="display: flex; align-items: center; gap: 10px;">
            <button type="button" class="csh-back-arrow" (click)="irAVista('inicio')">←</button>
            <h1 class="tiempos-main-h1">Calendario Quirúrgico, Agenda Médica y Biometría OD / OI</h1>
          </div>
          <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
            <button
              type="button"
              class="cs-btn-outlined"
              [class.active-filter]="vistaCalendario() === 'semana'"
              (click)="vistaCalendario.set('semana')"
            >
              📅 Vista Semanal
            </button>
            <button
              type="button"
              class="cs-btn-outlined"
              [class.active-filter]="vistaCalendario() === 'dia'"
              (click)="vistaCalendario.set('dia')"
            >
              🕒 Agenda del Día
            </button>
            <button
              type="button"
              class="cs-btn-outlined"
              [class.active-filter]="vistaCalendario() === 'quirofanos'"
              (click)="vistaCalendario.set('quirofanos')"
            >
              🏥 Solo Quirófanos
            </button>
            <button type="button" class="cs-btn-outlined" (click)="abrirCotizacionPdf(casoActualVista())">
              📄 Orden / Cotización PDF
            </button>
            <button type="button" class="cs-btn-blue" (click)="irAVista('casos')">
              Ir a Ficha de Caso
            </button>
          </div>
        </div>

        <!-- 4 Tarjetas Superiores de Biometría Ocular y Estado de Salas -->
        <div class="casos-top-summary-grid" style="grid-template-columns: repeat(4, 1fr);">
          <div class="cs-card csum-card">
            <div class="csum-label">Ojo Derecho (OD) · Pentacam</div>
            <div class="csum-main-row">
              <span class="csum-big">K1 {{ biometriaActual().odMax }} D</span>
              <span class="csum-pill blue">K2 {{ biometriaActual().odMay }} D</span>
            </div>
            <div class="csum-sub">Paciente: {{ limpiarNombre(casoActualVista().contacto) }} · Cil -0.50 D</div>
          </div>

          <div class="cs-card csum-card">
            <div class="csum-label">Ojo Izquierdo (OI) · Pentacam</div>
            <div class="csum-main-row">
              <span class="csum-big">K1 {{ biometriaActual().oiMax }} D</span>
              <span class="csum-pill blue">K2 {{ biometriaActual().oiMay }} D</span>
            </div>
            <div class="csum-sub">Eje 175° · Paquimetría 542 µm normal</div>
          </div>

          <div class="cs-card csum-card">
            <div class="csum-label">Lente Intraocular Calculado (LIO)</div>
            <div class="csum-main-row">
              <span class="csum-big">+{{ biometriaActual().okMax }} D</span>
              <span class="csum-pill green">{{ etiquetaProcedimiento(casoActualVista()) }}</span>
            </div>
            <div class="csum-sub">Fórmula Barrett Universal II · Biometría validada</div>
          </div>

          <div class="cs-card csum-card">
            <div class="csum-label">Programación Activa en Agenda</div>
            <div class="csum-main-row">
              <span class="csum-big">{{ citasAgendaFiltradas().length }} citas</span>
              <span class="csum-pill green">3 Especialistas</span>
            </div>
            <div class="csum-sub">Quirófano 1, Quirófano 2 y Unidad Pentacam/OCT</div>
          </div>
        </div>

        <!-- Panel Principal: Agendador Rápido + Calendario Visual Semanal -->
        <section class="cs-card casos-board-card">
          <div class="casos-board-header">
            <div>
              <h2 class="casos-board-title">Programar Nueva Cita o Cirugía para {{ limpiarNombre(casoActualVista().contacto) }}</h2>
              <p class="casos-board-sub">Selecciona día, hora y especialista para agendar inmediatamente en el calendario clínico</p>
            </div>
            <div class="casos-board-controls">
              <span style="font-size: 11.5px; font-weight: 600; color: #475569;">Filtrar por especialista:</span>
              <select
                class="cs-inline-select casos-proc-select"
                [ngModel]="filtroEspecialistaAgenda()"
                (ngModelChange)="filtroEspecialistaAgenda.set($event)"
              >
                <option value="todos">Todos los especialistas ({{ citasAgendaLocal().length }})</option>
                <option value="Dr. Javier Méndez">Dr. Javier Méndez — Catarata y Faco</option>
                <option value="Dra. Laura Gómez">Dra. Laura Gómez — Córnea y LASIK</option>
                <option value="Dr. Carlos Restrepo">Dr. Carlos Restrepo — Retina y OCT</option>
              </select>
            </div>
          </div>

          <!-- Barra de Agendamiento en 1 Clic -->
          <div class="agenda-form-bar">
            <select class="cs-inline-select casos-proc-select" [ngModel]="nuevaCitaDia()" (ngModelChange)="nuevaCitaDia.set($event)">
              <option value="Lunes">Lunes</option>
              <option value="Martes">Martes</option>
              <option value="Miércoles">Miércoles</option>
              <option value="Jueves">Jueves</option>
              <option value="Viernes">Viernes</option>
              <option value="Sábado">Sábado</option>
            </select>

            <select class="cs-inline-select casos-proc-select" [ngModel]="nuevaCitaHora()" (ngModelChange)="nuevaCitaHora.set($event)">
              <option value="08:00 AM">08:00 AM</option>
              <option value="09:00 AM">09:00 AM</option>
              <option value="10:00 AM">10:00 AM</option>
              <option value="11:00 AM">11:00 AM</option>
              <option value="02:00 PM">02:00 PM</option>
              <option value="03:30 PM">03:30 PM</option>
            </select>

            <select class="cs-inline-select casos-proc-select" [ngModel]="nuevaCitaEspecialista()" (ngModelChange)="nuevaCitaEspecialista.set($event)">
              <option value="Dr. Javier Méndez">Dr. Javier Méndez (Catarata)</option>
              <option value="Dra. Laura Gómez">Dra. Laura Gómez (Córnea/LASIK)</option>
              <option value="Dr. Carlos Restrepo">Dr. Carlos Restrepo (Retina/OCT)</option>
            </select>

            <input
              type="text"
              class="cs-clean-input"
              placeholder="Procedimiento o motivo de cita..."
              [ngModel]="nuevaCitaTipo()"
              (ngModelChange)="nuevaCitaTipo.set($event)"
            />

            <button type="button" class="cs-btn-green" (click)="agendarCitaRapida()">
              + Agendar Cita Ahora
            </button>
          </div>

          <!-- Cuadrícula de Calendario Semanal (Lunes a Sábado) -->
          <div class="agenda-week-grid">
            @for (dia of ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado']; track dia) {
              <div class="agenda-day-col">
                <div class="agenda-day-head">
                  <span>{{ dia }}</span>
                  <span class="agenda-day-count">{{ citasPorDia(dia).length }}</span>
                </div>

                @for (cita of citasPorDia(dia); track cita.id) {
                  <div class="agenda-event-card" [attr.data-tone]="cita.tono">
                    <div class="aec-time">{{ cita.hora }} · {{ cita.convenio }}</div>
                    <div class="aec-pat">{{ cita.paciente }}</div>
                    <div class="aec-proc">{{ cita.procedimiento }}</div>
                    <div class="aec-doc">👨‍⚕️ {{ cita.especialista }}</div>
                    <div class="aec-doc">📍 {{ cita.sala }}</div>
                  </div>
                } @empty {
                  <div style="font-size: 11px; color: #94A3B8; padding: 14px 4px; text-align: center;">
                    Cupos disponibles
                  </div>
                }
              </div>
            }
          </div>
        </section>
      }

      <!-- =====================================================================
           VISTA 6: ACTIVIDAD Y SEGUIMIENTO DIARIO (3 PESTAÑAS CLARAS Y SEPARADAS)
           ===================================================================== -->
      @else {
        <div class="tiempos-title-bar">
          <div style="display: flex; align-items: center; gap: 10px;">
            <button type="button" class="csh-back-arrow" (click)="irAVista('inicio')">←</button>
            <h1 class="tiempos-main-h1">Actividad y Seguimiento Diario</h1>
          </div>
          <div style="display: flex; align-items: center; gap: 8px;">
            <button type="button" class="cs-btn-outlined" (click)="exportarCasosExcel()">
              📤 Exportar Excel
            </button>
            <button type="button" class="cs-btn-blue" (click)="irAVista('casos')">
              Ir a Casos Clínicos
            </button>
          </div>
        </div>

        <!-- BARRA DE 3 PESTAÑAS CLARAS (SOLO VES UNA COSA A LA VEZ) -->
        <div class="act-tabs-bar">
          <button
            type="button"
            class="act-tab-pill"
            [class.active]="pestanaActividad() === 'tareas'"
            (click)="pestanaActividad.set('tareas')"
          >
            📋 Tareas de Hoy y Bitácora ({{ tareasPendientesHoyCount() }})
          </button>
          <button
            type="button"
            class="act-tab-pill"
            [class.active]="pestanaActividad() === 'reportes'"
            (click)="pestanaActividad.set('reportes')"
          >
            👩‍⚕️ Rendimiento por Asesora
          </button>
          <button
            type="button"
            class="act-tab-pill"
            [class.active]="pestanaActividad() === 'automatizaciones'"
            (click)="pestanaActividad.set('automatizaciones')"
          >
            ⚡ Recordatorios Automáticos ({{ reglasAutomatizacion().length }})
          </button>
        </div>

        <!-- PESTAÑA 1 (PRINCIPAL): ¿QUÉ TENEMOS PENDIENTE POR HACER HOY? + BITÁCORA EN VIVO -->
        @if (pestanaActividad() === 'tareas') {
          <div class="act-clear-grid">
            <!-- Columna izquierda (65%): Lista clara de tareas del día agrupada en 3 bloques -->
            <section class="cs-card act-main-card">
              <div class="act-card-head">
                <div>
                  <h2 class="act-card-title">¿Qué tenemos pendiente por hacer hoy?</h2>
                  <p class="act-card-sub">
                    Marca la casilla al completar una gestión o haz clic en "Resolver ahora →" para atender al paciente
                  </p>
                </div>
              </div>

              <!-- GRUPO 1: PACIENTES POR CONFIRMAR CITA / CIRUGÍA -->
              <div class="act-checklist-group">
                <div class="act-group-head">
                  <span class="act-group-title">
                    1. Pacientes por confirmar cita o cirugía de mañana ({{ grupoTareasConfirmar().length }})
                  </span>
                </div>
                <div class="act-rows-box">
                  @for (c of grupoTareasConfirmar(); track c.id) {
                    <div class="act-check-row" [class.done]="estaTareaCompletada(c.id)">
                      <input
                        type="checkbox"
                        class="act-checkbox"
                        [checked]="estaTareaCompletada(c.id)"
                        (change)="marcarTareaCompletada(c, 'Confirmación de cita/cirugía')"
                      />
                      <span class="act-pat-name">{{ limpiarNombre(c.contacto) }}</span>
                      <span class="act-proc-name">{{ c.titulo }}</span>
                      <span class="act-coord-name">Asesora: {{ c.responsable || 'Dra. Laura Gómez' }}</span>
                      <button
                        type="button"
                        class="act-resolve-btn"
                        (click)="resolverTareaAhora(c)"
                      >
                        {{ estaTareaCompletada(c.id) ? '✓ Gestionado' : 'Resolver ahora →' }}
                      </button>
                    </div>
                  }
                </div>
              </div>

              <!-- GRUPO 2: SEGUIMIENTOS DE ÓRDENES Y AUTORIZACIONES -->
              <div class="act-checklist-group">
                <div class="act-group-head">
                  <span class="act-group-title">
                    2. Seguimientos de órdenes y autorizaciones médicas ({{ grupoTareasAutorizaciones().length }})
                  </span>
                </div>
                <div class="act-rows-box">
                  @for (c of grupoTareasAutorizaciones(); track c.id) {
                    <div class="act-check-row" [class.done]="estaTareaCompletada(c.id)">
                      <input
                        type="checkbox"
                        class="act-checkbox"
                        [checked]="estaTareaCompletada(c.id)"
                        (change)="marcarTareaCompletada(c, 'Validación de orden y autorización')"
                      />
                      <span class="act-pat-name">{{ limpiarNombre(c.contacto) }}</span>
                      <span class="act-proc-name">{{ c.titulo }} · {{ obtenerConvenioCaso(c) }}</span>
                      <span class="act-coord-name">Asesora: {{ c.responsable || 'Dra. Laura Gómez' }}</span>
                      <button
                        type="button"
                        class="act-resolve-btn"
                        (click)="resolverTareaAhora(c)"
                      >
                        {{ estaTareaCompletada(c.id) ? '✓ Gestionado' : 'Resolver ahora →' }}
                      </button>
                    </div>
                  }
                </div>
              </div>

              <!-- GRUPO 3: RESPUESTAS PENDIENTES EN REDES Y WHATSAPP -->
              <div class="act-checklist-group">
                <div class="act-group-head">
                  <span class="act-group-title">
                    3. Respuestas pendientes en Redes Sociales y WhatsApp ({{ grupoTareasRedes().length }})
                  </span>
                </div>
                <div class="act-rows-box">
                  @for (c of grupoTareasRedes(); track c.id) {
                    <div class="act-check-row" [class.done]="estaTareaCompletada(c.id)">
                      <input
                        type="checkbox"
                        class="act-checkbox"
                        [checked]="estaTareaCompletada(c.id)"
                        (change)="marcarTareaCompletada(c, 'Respuesta en Redes y WhatsApp')"
                      />
                      <span class="act-pat-name">{{ limpiarNombre(c.contacto) }}</span>
                      <span class="act-proc-name">{{ c.titulo }} — Canal: {{ (c.origen || 'WhatsApp') | uppercase }}</span>
                      <span class="act-coord-name">Asesora: {{ c.responsable || 'Coordinación Qx' }}</span>
                      <button
                        type="button"
                        class="act-resolve-btn"
                        (click)="abrirConversacion(c.id)"
                      >
                        Responder ahora →
                      </button>
                    </div>
                  }
                </div>
              </div>
            </section>

            <!-- Columna derecha (35%): Actividad Reciente del Equipo en Vivo -->
            <aside class="cs-card act-feed-card">
              <div class="act-card-head">
                <div>
                  <h2 class="act-card-title">Actividad Reciente del Equipo (En vivo)</h2>
                  <p class="act-card-sub">Últimas gestiones realizadas hoy por las asesoras</p>
                </div>
              </div>

              <div class="act-live-feed">
                @for (item of bitacoraEquipoReciente(); track item.id) {
                  <div class="act-feed-item">
                    <div class="act-feed-avatar" [attr.data-tone]="item.tono">{{ item.iniciales }}</div>
                    <div class="act-feed-body">
                      <div class="act-feed-text">
                        <span class="act-feed-time">{{ item.hace }} -</span>
                        <b>{{ item.asesora }}</b> {{ item.accion }}
                      </div>
                      <div class="act-feed-sub">{{ item.detalle }}</div>
                    </div>
                  </div>
                }
              </div>
            </aside>
          </div>
        }

        <!-- PESTAÑA 2: RENDIMIENTO POR ASESORA Y MOTIVOS DE ESPERA -->
        @else if (pestanaActividad() === 'reportes') {
          <div class="tiempos-mid-grid">
            <section class="cs-card tiempos-mid-card">
              <div class="tmid-title">Productividad y Pacientes Gestionados por Asesora</div>
              <div class="tmid-list">
                @for (coord of coordinadoresTurno(); track coord.nombre) {
                  <div class="tmid-row">
                    <div class="tmid-row-top">
                      <span class="tmid-name">{{ coord.nombre }} ({{ coord.casos }} pacientes asignados)</span>
                      <span class="tmid-pct">{{ coord.cumplimiento }}% al día</span>
                    </div>
                    <div class="tmid-bar-bg">
                      <div class="tmid-bar-fill green" [style.width.%]="coord.cumplimiento"></div>
                    </div>
                  </div>
                }
              </div>
            </section>

            <section class="cs-card tiempos-mid-card">
              <div class="tmid-title">¿Por qué algunos pacientes siguen en espera?</div>
              <div class="tmid-list">
                @for (m of motivosDescarte(); track m.motivo) {
                  <div class="tmid-row">
                    <div class="tmid-row-top">
                      <span class="tmid-name">{{ m.motivo }} ({{ m.cantidad }} pacientes)</span>
                      <span class="tmid-pct">{{ m.porcentaje }}%</span>
                    </div>
                    <div class="tmid-bar-bg">
                      <div class="tmid-bar-fill" [style.width.%]="m.porcentaje" [style.background]="m.color"></div>
                    </div>
                  </div>
                }
              </div>
            </section>
          </div>
        }

        <!-- PESTAÑA 3: RECORDATORIOS AUTOMÁTICOS Y AVISOS EN BLOQUE -->
        @else {
          <section class="cs-card casos-board-card">
            <div class="casos-board-header">
              <div>
                <h2 class="casos-board-title">⚡ Recordatorios Automáticos a Pacientes</h2>
                <p class="casos-board-sub">
                  Activa o pausa con 1 clic los mensajes automáticos que el CRM envía para evitar inasistencias
                </p>
              </div>
            </div>

            <div class="agenda-form-bar" style="grid-template-columns: 1.1fr 1fr 1fr auto;">
              <input
                type="text"
                class="cs-clean-input"
                placeholder="Nuevo recordatorio (ej. Confirmación de examen Pentacam 24h antes)..."
                [(ngModel)]="nuevaReglaTitulo"
              />
              <select class="cs-inline-select casos-proc-select" [(ngModel)]="nuevaReglaDisparador">
                <option value="Al avanzar etapa a &quot;Validación de Autorización&quot;">Cuando pasa a Validación de Autorización</option>
                <option value="Al avanzar etapa a &quot;Programación de Cirugía&quot;">Cuando pasa a Programación de Cirugía</option>
                <option value="Si lleva 48h sin respuesta del paciente">Si lleva 48h sin respuesta del paciente</option>
              </select>
              <select class="cs-inline-select casos-proc-select" [(ngModel)]="nuevaReglaAccion">
                <option value="Enviar mensaje de confirmación por WhatsApp y etiquetar caso">Enviar WhatsApp + Marcar Prioritario</option>
                <option value="Asignar automáticamente en turno (Round-Robin) a coordinadora">Asignar a asesora en turno</option>
                <option value="Generar orden de autorización y notificar a Convenios">Notificar a Convenios y Pólizas</option>
              </select>
              <button type="button" class="cs-btn-green" (click)="crearReglaAutomatizacion()">
                + Crear Recordatorio
              </button>
            </div>

            <div class="auto-rules-list">
              @for (regla of reglasAutomatizacion(); track regla.id) {
                <div class="auto-rule-card" [class.inactive]="!regla.activa">
                  <div>
                    <div class="arc-title">{{ regla.titulo }}</div>
                    <div class="arc-meta">
                      <span><b>Cuándo se envía:</b> {{ regla.disparador }}</span>
                      <span>→</span>
                      <span><b>Qué hace:</b> {{ regla.accion }}</span>
                    </div>
                  </div>

                  <div style="display: flex; align-items: center; gap: 10px;">
                    <span style="font-size: 11.5px; font-weight: 700; color: #475569;">
                      {{ regla.ejecuciones }} enviados
                    </span>
                    <button
                      type="button"
                      class="cs-btn-outlined"
                      [style.background]="regla.activa ? '#DCFCE7' : '#F1F5F9'"
                      [style.color]="regla.activa ? '#15803D' : '#64748B'"
                      (click)="alternarReglaAutomatizacion(regla.id)"
                    >
                      {{ regla.activa ? '✓ Activo' : 'Pausado' }}
                    </button>
                  </div>
                </div>
              }
            </div>
          </section>
        }
      }

      <!-- =====================================================================
           MODAL GLOBAL: COTIZACIÓN QUIRÚRGICA Y ORDEN MÉDICA EN PDF (1 CLIC)
           ===================================================================== -->
      @if (cotizacionModalCaso(); as pdfCaso) {
        <div class="pdf-modal-backdrop" (click)="cerrarCotizacionPdf()">
          <div class="pdf-sheet" (click)="$event.stopPropagation()">
            <div class="pdf-head">
              <div>
                <div class="pdf-clinic-name">CENTRO OFTALMOLÓGICO EBENEZER</div>
                <div class="pdf-clinic-sub">NIT 900.412.890-1 · Unidad de Cirugía Oftalmológica, Facoemulsificación y Diagnóstico Ocular</div>
                <div class="pdf-clinic-sub">NexaMed CRM · Documento Clínico Oficial</div>
              </div>
              <div class="pdf-doc-box">
                <div class="pdf-doc-title">Cotización / Orden Quirúrgica</div>
                <div class="pdf-doc-num">{{ codigoRadicadoConvenio(pdfCaso, 0) }}</div>
              </div>
            </div>

            <div class="pdf-patient-grid">
              <div>
                <div style="color: #64748B; font-size: 10.5px;">PACIENTE</div>
                <b>{{ limpiarNombre(pdfCaso.contacto) }}</b>
              </div>
              <div>
                <div style="color: #64748B; font-size: 10.5px;">DOCUMENTO / TELÉFONO</div>
                <b>{{ pdfCaso.contactoDocumento || 'CC 1000000015' }} · {{ pdfCaso.contactoTelefono || '+57 300 412 8899' }}</b>
              </div>
              <div>
                <div style="color: #64748B; font-size: 10.5px;">CONVENIO / ASEGURADORA</div>
                <b>{{ calcularDesgloseCotizacion(pdfCaso).convenio }} ({{ calcularDesgloseCotizacion(pdfCaso).coberturaPct }}% cobertura)</b>
              </div>
              <div>
                <div style="color: #64748B; font-size: 10.5px;">PROCESO CLÍNICO</div>
                <b>{{ pdfCaso.embudo }}</b>
              </div>
              <div>
                <div style="color: #64748B; font-size: 10.5px;">BIOMETRÍA OD / OI &amp; LIO</div>
                <b>OD {{ biometriaActual().odMax }}D · OI {{ biometriaActual().oiMax }}D · LIO +{{ biometriaActual().okMax }}D</b>
              </div>
              <div>
                <div style="color: #64748B; font-size: 10.5px;">ESPECIALISTA ASIGNADO</div>
                <b>{{ pdfCaso.responsable || 'Dra. Laura Gómez' }}</b>
              </div>
            </div>

            <table class="ttab-table" style="border: 1px solid #E2E8F0; border-radius: 8px; overflow: hidden;">
              <thead>
                <tr style="background: #F1F5F9;">
                  <th>Código CUPS</th>
                  <th>Concepto Clínico / Quirúrgico</th>
                  <th style="text-align: right;">Valor Tarifa (COP)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><b>{{ calcularDesgloseCotizacion(pdfCaso).codigoCups }}</b></td>
                  <td>{{ pdfCaso.titulo }} — Honorarios quirúrgicos y equipo oftalmológico</td>
                  <td style="text-align: right;">$ {{ calcularDesgloseCotizacion(pdfCaso).baseQx.toLocaleString('es-CO') }}</td>
                </tr>
                <tr>
                  <td><b>INS-LIO-2026</b></td>
                  <td>Lente Intraocular Premium +{{ biometriaActual().okMax }} D ({{ etiquetaProcedimiento(pdfCaso) }}) e insumos</td>
                  <td style="text-align: right;">$ {{ calcularDesgloseCotizacion(pdfCaso).lenteValor.toLocaleString('es-CO') }}</td>
                </tr>
                <tr>
                  <td><b>DER-SALA-QX</b></td>
                  <td>Derechos de sala de cirugía, microscopía quirúrgica y controles post-operatorios</td>
                  <td style="text-align: right;">$ {{ calcularDesgloseCotizacion(pdfCaso).salaValor.toLocaleString('es-CO') }}</td>
                </tr>
              </tbody>
            </table>

            <div class="pdf-totals-box">
              <div class="pdf-total-row">
                <span>Subtotal Procedimiento:</span>
                <b>$ {{ calcularDesgloseCotizacion(pdfCaso).subtotal.toLocaleString('es-CO') }}</b>
              </div>
              <div class="pdf-total-row">
                <span>Cobertura {{ calcularDesgloseCotizacion(pdfCaso).convenio }} ({{ calcularDesgloseCotizacion(pdfCaso).coberturaPct }}%):</span>
                <span style="color: #1D4ED8;">-$ {{ calcularDesgloseCotizacion(pdfCaso).descuentoPoliza.toLocaleString('es-CO') }}</span>
              </div>
              <div class="pdf-total-row final">
                <span>Valor Copago / Neto Paciente:</span>
                <span>$ {{ calcularDesgloseCotizacion(pdfCaso).copagoFinal.toLocaleString('es-CO') }}</span>
              </div>
            </div>

            <div class="pdf-actions-bar">
              <button type="button" class="cs-btn-outlined" (click)="cerrarCotizacionPdf()">
                Cerrar
              </button>
              <button type="button" class="cs-btn-blue" (click)="enviarCotizacionAlChat(pdfCaso)">
                💬 Enviar Resumen al Historial / Chat
              </button>
              <button type="button" class="cs-btn-green" (click)="imprimirCotizacionPdf()">
                🖨️ Imprimir / Guardar en PDF
              </button>
            </div>
          </div>
        </div>
      }
    </div>
  `,
  styles: [`
    .cs-studio-page {
      padding: 12px 16px 24px;
      display: flex;
      flex-direction: column;
      gap: 12px;
      min-height: 100%;
    }

    /* 4 KPIs Ejecutivos en Vista Inicio */
    .cs-exec-kpis {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 10px;
    }

    .cs-exec-kpi {
      border-radius: 8px;
      padding: 12px 16px;
      color: #FFFFFF;
      cursor: pointer;
      transition: transform .12s;
    }
    .cs-exec-kpi:hover { transform: translateY(-1px); }
    .cs-exec-kpi.sky    { background: #0284C7; }
    .cs-exec-kpi.green  { background: #16A34A; }
    .cs-exec-kpi.orange { background: #EA580C; }
    .cs-exec-kpi.royal  { background: #1D4ED8; }

    .cek-lbl { font-size: 11px; opacity: .9; }
    .cek-val { font-size: 24px; font-weight: 700; margin: 2px 0; }
    .cek-sub { font-size: 10.5px; opacity: .85; }

    .cs-proc-cards-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 10px;
    }

    .cs-proc-summary-card {
      padding: 11px 14px;
      border-radius: 6px;
      border: 1px solid #E2E8F0;
      background: #F8FAFC;
      cursor: pointer;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    .cs-proc-summary-card:hover {
      border-color: #006CE0;
      background: #EFF6FF;
    }
    .cpsc-name { font-size: 12.5px; font-weight: 600; color: #0F172A; }
    .cpsc-count { font-size: 11px; color: #64748B; }
    .cpsc-link { font-size: 11px; color: #006CE0; font-weight: 600; margin-top: 2px; }

    /* =====================================================================
       1. CABECERA BLANCA COMPACTA (CREATIO 8 STUDIO)
       ===================================================================== */
    .cs-header-card {
      background: #FFFFFF;
      border-radius: 8px;
      border: 1px solid #E2E7EE;
      padding: 12px 18px 10px;
      box-shadow: 0 1px 3px rgba(15, 23, 42, .03);
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .csh-top-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      flex-wrap: wrap;
    }

    .csh-title-zone {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .csh-back-arrow {
      border: 0;
      background: transparent;
      color: #006CE0;
      font-size: 16px;
      font-weight: 700;
      cursor: pointer;
      padding: 0 4px;
    }

    .csh-record-title {
      margin: 0;
      font-size: 17px;
      font-weight: 700;
      color: #0F2942;
      letter-spacing: -.01em;
    }

    .csh-buttons-zone {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .cs-btn-blue {
      height: 28px;
      padding: 0 13px;
      border-radius: 4px;
      border: 0;
      background: #006CE0;
      color: #FFFFFF;
      font: 600 11.5px var(--font);
      cursor: pointer;
    }
    .cs-btn-blue:hover { background: #0058B8; }

    .cs-btn-outlined {
      height: 28px;
      padding: 0 12px;
      border-radius: 4px;
      border: 1px solid #CBD5E1;
      background: #FFFFFF;
      color: #334155;
      font: 600 11.5px var(--font);
      cursor: pointer;
    }
    .cs-btn-outlined:hover,
    .cs-btn-outlined.active-filter {
      background: #EFF6FF;
      border-color: #006CE0;
      color: #006CE0;
    }

    .cs-btn-green {
      height: 28px;
      padding: 0 13px;
      border-radius: 4px;
      border: 0;
      background: #208B3A;
      color: #FFFFFF;
      font: 600 11.5px var(--font);
      cursor: pointer;
    }
    .cs-btn-green:hover { background: #19722E; }

    .cs-btn-dots {
      height: 28px;
      width: 28px;
      border-radius: 4px;
      border: 1px solid #E2E8F0;
      background: #F8FAFC;
      color: #475569;
      font-size: 11px;
      cursor: pointer;
    }

    .csh-bottom-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
    }

    .csh-tags-area {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }

    .csh-add-tag-link {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      border: 0;
      background: transparent;
      color: #1E3A8A;
      font: 500 11.5px var(--font);
      cursor: pointer;
      padding: 0;
    }
    .csh-add-tag-link span {
      color: #64748B;
    }

    .cs-mini-tag {
      font-size: 10.5px;
      font-weight: 500;
      color: #475569;
      background: #F1F5F9;
      border: 1px solid #E2E8F0;
      padding: 1px 7px;
      border-radius: 4px;
    }
    .cs-mini-tag.removable {
      cursor: pointer;
    }

    .csh-feed-links {
      display: flex;
      align-items: center;
      gap: 14px;
    }

    .csh-util-link {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      border: 0;
      background: transparent;
      color: #1E293B;
      font: 500 11px var(--font);
      cursor: pointer;
    }
    .csh-util-link svg {
      color: #0F2942;
    }

    /* =====================================================================
       2. CUERPO INTERNO DE 2 COLUMNAS (245px IZQUIERDA | 1fr DERECHA)
       ===================================================================== */
    .cs-two-col-body {
      display: grid;
      grid-template-columns: 245px minmax(0, 1fr);
      gap: 12px;
      align-items: start;
    }

    .cs-card {
      background: #FFFFFF;
      border-radius: 8px;
      border: 1px solid #E2E7EE;
      box-shadow: 0 1px 3px rgba(15, 23, 42, .03);
    }

    .cs-left-col {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .cs-fields-card {
      padding: 14px 16px;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .cs-field-group {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .cs-field-label {
      font-size: 11px;
      color: #64748B;
      font-weight: 400;
    }

    .cs-req {
      color: #EF4444;
      margin-left: 2px;
    }

    .cs-underlined-val {
      font-size: 12px;
      color: #0F172A;
      font-weight: 500;
      padding: 3px 0 5px;
      border-bottom: 1px solid #ECEFF5;
    }

    .cs-underlined-select {
      border: 0;
      border-bottom: 1px solid #ECEFF5;
      background: transparent;
      font: 500 12px var(--font);
      color: #0F172A;
      padding: 3px 0 5px;
      outline: none;
      cursor: pointer;
    }

    .cs-done-status {
      display: flex;
      flex-direction: column;
      gap: 4px;
      margin-top: 2px;
    }

    .cs-done-text {
      font-size: 11.5px;
      color: #1E293B;
      font-weight: 500;
    }

    .cs-done-green-bar {
      height: 3px;
      width: 78%;
      background: #4CAF50;
      border-radius: 2px;
    }

    .cs-blue-check-row {
      display: flex;
      align-items: center;
      gap: 7px;
      font-size: 11px;
      color: #94A3B8;
      cursor: pointer;
    }

    .cs-blue-check-row input {
      accent-color: #006CE0;
      width: 14px;
      height: 14px;
    }

    .cs-consult-banner {
      border-radius: 8px;
      padding: 16px;
      background:
        radial-gradient(circle at 85% 20%, rgba(96, 165, 250, .35) 0%, transparent 55%),
        linear-gradient(135deg, #1E3A8A 0%, #1D4ED8 60%, #0F2942 100%);
      color: #FFFFFF;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
      box-shadow: 0 2px 6px rgba(15, 23, 42, .12);
    }

    .cs-consult-text {
      font-size: 12px;
      line-height: 1.4;
      color: #F8FAFC;
      font-weight: 500;
    }

    .cs-consult-btn {
      height: 28px;
      padding: 0 14px;
      border-radius: 4px;
      border: 0;
      background: #006CE0;
      color: #FFFFFF;
      font: 600 11.5px var(--font);
      cursor: pointer;
    }
    .cs-consult-btn:hover { background: #0058B8; }

    .cs-faq-card {
      padding: 14px 16px;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .cs-faq-title {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 13px;
      font-weight: 600;
      color: #1E293B;
    }

    .cs-faq-q {
      width: 15px;
      height: 15px;
      border-radius: 50%;
      border: 1px solid #64748B;
      color: #64748B;
      font-size: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .cs-faq-links {
      display: flex;
      flex-direction: column;
      gap: 7px;
      padding-left: 20px;
    }

    .cs-faq-links a {
      font-size: 11.5px;
      color: #006CE0;
      cursor: pointer;
      text-decoration: none;
      line-height: 1.35;
    }
    .cs-faq-links a:hover {
      text-decoration: underline;
    }

    .cs-main-panel {
      padding: 10px 22px 24px;
      min-height: 500px;
      display: flex;
      flex-direction: column;
      gap: 18px;
    }

    .cs-tabs-strip {
      display: flex;
      align-items: center;
      gap: 20px;
      border-bottom: 1px solid #E2E8F0;
    }

    .cs-tab-btn {
      border: 0;
      background: transparent;
      padding: 8px 0 9px;
      font: 600 11px var(--font);
      color: #006CE0;
      letter-spacing: .03em;
      cursor: pointer;
      border-bottom: 2px solid transparent;
      margin-bottom: -1px;
      opacity: .78;
    }
    .cs-tab-btn:hover { opacity: 1; }
    .cs-tab-btn.active {
      opacity: 1;
      font-weight: 700;
      border-bottom-color: #006CE0;
    }

    .cs-sec-block {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .cs-sec-header {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      user-select: none;
    }

    .cs-sq-chevron {
      width: 15px;
      height: 15px;
      border-radius: 3px;
      font-size: 10px;
      font-weight: 800;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #FFFFFF;
    }
    .cs-sq-chevron.orange { background: #F05A28; }
    .cs-sq-chevron.gray   { background: #94A3B8; }

    .cs-sec-title {
      font-size: 12.5px;
      font-weight: 600;
      color: #1E3A5F;
    }

    .cs-plus-inline {
      font-size: 15px;
      font-weight: 600;
      color: #1E3A5F;
      margin-left: 2px;
    }

    .cs-info-i {
      font-size: 12px;
      color: #3B82F6;
    }

    .cs-sec-content {
      padding-left: 22px;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .cs-actions-mini {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 11px;
      color: #64748B;
    }

    .cs-inline-select {
      border: 0;
      background: transparent;
      font: 500 11.5px var(--font);
      color: #1E293B;
      cursor: pointer;
      outline: none;
    }

    .cs-condition-row {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .cs-and-pill {
      font-size: 11px;
      font-weight: 600;
      color: #1E40AF;
      background: #DBEAFE;
      padding: 2px 8px;
      border-radius: 4px;
    }

    .cs-condition-box {
      flex: 1;
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 6px 10px;
      border-bottom: 1px solid #F1F5F9;
      flex-wrap: wrap;
    }

    .cs-condition-box input {
      accent-color: #006CE0;
      width: 14px;
      height: 14px;
    }

    .cs-cond-col {
      font-size: 11.5px;
      color: #64748B;
    }

    .cs-cond-eq {
      font-size: 12px;
      color: #64748B;
      margin: 0 8px;
    }

    .cs-stage-steps-inline {
      display: flex;
      align-items: center;
      gap: 6px;
      flex-wrap: wrap;
    }

    .cs-inline-stage-chip {
      border: 1px solid #E2E8F0;
      background: #F8FAFC;
      color: #475569;
      font: 500 11px var(--font);
      padding: 2px 8px;
      border-radius: 4px;
      cursor: pointer;
    }
    .cs-inline-stage-chip.done {
      background: #DCFCE7;
      border-color: #BBF7D0;
      color: #15803D;
    }
    .cs-inline-stage-chip.current {
      background: #006CE0;
      border-color: #006CE0;
      color: #FFFFFF;
      font-weight: 600;
    }

    .cs-add-cond-btn {
      align-self: flex-start;
      border: 0;
      background: transparent;
      color: #1E3A5F;
      font: 500 11.5px var(--font);
      cursor: pointer;
      padding: 2px 0;
    }
    .cs-add-cond-btn:hover { color: #006CE0; }

    .cs-t-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .cs-t-row {
      display: flex;
      align-items: center;
      gap: 9px;
      padding: 5px 8px;
      border-radius: 5px;
      font-size: 12px;
      color: #1E293B;
    }

    .cs-t-row.interactive {
      cursor: pointer;
      transition: background .12s;
    }
    .cs-t-row.interactive:hover {
      background: #F8FAFC;
    }
    .cs-t-row.interactive.selected {
      background: #EFF6FF;
    }

    .cs-t-icon {
      font-family: Georgia, 'Times New Roman', serif;
      font-size: 12px;
      font-weight: 700;
      color: #0284C7;
      width: 14px;
      text-align: center;
      flex-shrink: 0;
    }

    .cs-t-patient {
      font-weight: 600;
      color: #0F172A;
    }

    .cs-t-sep {
      color: #94A3B8;
    }

    .cs-t-proc {
      color: #1E293B;
    }

    .cs-t-meta {
      font-size: 11px;
      color: #64748B;
    }

    .cs-t-actions {
      margin-left: auto;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .cs-sla-text {
      font-size: 10.5px;
      color: #15803D;
      font-weight: 500;
    }
    .cs-sla-text.urgent {
      color: #DC2626;
    }

    .cs-mini-step-btn {
      border: 1px solid #CBD5E1;
      background: #FFFFFF;
      color: #1E293B;
      font: 500 10.5px var(--font);
      padding: 2px 8px;
      border-radius: 4px;
      cursor: pointer;
    }
    .cs-mini-step-btn:hover {
      border-color: #006CE0;
      color: #006CE0;
    }
    .cs-mini-step-btn.done {
      border-color: #208B3A;
      color: #208B3A;
    }

    .cs-quick-reply-row {
      display: flex;
      align-items: center;
      gap: 8px;
      max-width: 580px;
    }

    .cs-clean-input {
      flex: 1;
      height: 28px;
      border: 0;
      border-bottom: 1px solid #CBD5E1;
      font: 400 12px var(--font);
      color: #0F172A;
      outline: none;
      background: transparent;
    }
    .cs-clean-input:focus {
      border-bottom-color: #006CE0;
    }

    /* ESTILOS VISTA 3: CONTROL DE TIEMPOS Y PRIORIDADES (NEXAMED CRM STUDIO) */
    .tiempos-title-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 2px 2px 4px;
    }

    .tiempos-main-h1 {
      margin: 0;
      font-size: 18px;
      font-weight: 700;
      color: #162032;
      letter-spacing: -0.02em;
    }

    .tiempos-kpi-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 10px;
    }

    .tkpi-card {
      background: #FFFFFF;
      border: 1px solid #DCE3EE;
      border-radius: 10px;
      padding: 12px 14px;
      box-shadow: 0 1px 3px rgba(15, 23, 42, 0.03);
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      gap: 10px;
      cursor: pointer;
      transition: transform 0.12s, box-shadow 0.12s, border-color 0.12s;
    }
    .tkpi-card:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
      border-color: #B6C8E2;
    }

    .tkpi-top {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .tkpi-ring-wrap {
      position: relative;
      width: 66px;
      height: 66px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .tkpi-svg {
      width: 66px;
      height: 66px;
      transform: rotate(-90deg);
    }

    .tkpi-bg {
      fill: none;
      stroke: #E8EEF6;
      stroke-width: 3.6;
    }

    .tkpi-stroke {
      fill: none;
      stroke-width: 3.6;
      stroke-linecap: round;
    }
    .tkpi-stroke.navy { stroke: #1D3557; }
    .tkpi-stroke.coral { stroke: #D94826; }
    .tkpi-stroke.green { stroke: #4C9A52; }

    .tkpi-ring-txt {
      position: absolute;
      font-size: 12px;
      font-weight: 800;
      color: #162032;
    }

    .tkpi-info {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .tkpi-big {
      font-size: 19px;
      font-weight: 800;
      color: #162032;
      line-height: 1.1;
    }
    .tkpi-big.coral-txt {
      color: #9A2B15;
    }

    .tkpi-desc {
      font-size: 10.5px;
      color: #475569;
      line-height: 1.2;
    }

    .tkpi-bars {
      display: flex;
      align-items: flex-end;
      gap: 3px;
      height: 18px;
      margin-top: 4px;
    }
    .tkpi-bars span {
      flex: 1;
      border-radius: 2px 2px 0 0;
      background: #CBD5E1;
    }
    .tkpi-bars.blue span { background: #3B6EA8; }
    .tkpi-bars.blue span:nth-child(even) { background: #7FA4CE; }
    .tkpi-bars.coral span { background: #DF6547; }
    .tkpi-bars.coral span:nth-child(even) { background: #F2B5A7; }
    .tkpi-bars.green span { background: #5CA462; }
    .tkpi-bars.green span:nth-child(even) { background: #A5CFA8; }

    .tkpi-foot {
      text-align: center;
      border-top: 1px solid #F1F5F9;
      padding-top: 7px;
      display: flex;
      flex-direction: column;
      gap: 1px;
    }
    .tkpi-foot b {
      font-size: 11px;
      font-weight: 700;
      color: #162032;
    }
    .tkpi-foot span {
      font-size: 10px;
      color: #64748B;
    }

    .tiempos-mid-grid {
      display: grid;
      grid-template-columns: 1.15fr 0.85fr;
      gap: 10px;
    }

    .tiempos-mid-card {
      padding: 13px 16px !important;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .tmid-title {
      font-size: 13px;
      font-weight: 700;
      color: #162032;
    }

    .tmid-bars-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .tmid-bar-row {
      display: grid;
      grid-template-columns: 185px 1fr 40px;
      align-items: center;
      gap: 10px;
      padding: 4px 6px;
      border-radius: 6px;
      cursor: pointer;
      transition: background 0.12s;
    }
    .tmid-bar-row:hover,
    .tmid-bar-row.selected {
      background: #EFF6FF;
    }

    .tmid-proc-lbl {
      font-size: 11.5px;
      font-weight: 500;
      color: #1E293B;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .tmid-bar-track {
      height: 8px;
      background: #E8EEF6;
      border-radius: 999px;
      overflow: hidden;
    }

    .tmid-bar-fill {
      height: 100%;
      border-radius: 999px;
      background: linear-gradient(90deg, #1E3A6D 0%, #4E5865 52%, #8A6236 100%);
    }

    .tmid-pct-val {
      font-size: 11.5px;
      font-weight: 700;
      color: #162032;
      text-align: right;
    }

    .tmid-coord-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .tmid-coord-row {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 6px 8px;
      border-radius: 8px;
      cursor: pointer;
      transition: background 0.12s;
    }
    .tmid-coord-row:hover,
    .tmid-coord-row.selected {
      background: #EFF6FF;
    }

    .tmid-avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: linear-gradient(135deg, #186ADE 0%, #0E2A4F 100%);
      color: #FFFFFF;
      font-size: 11px;
      font-weight: 700;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .tmid-coord-text {
      font-size: 12.5px;
      color: #334155;
    }
    .tmid-coord-text b {
      color: #162032;
      font-weight: 700;
    }
    .tmid-coord-text span {
      color: #15803D;
      font-weight: 600;
    }

    .tiempos-table-card {
      padding: 13px 16px !important;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .ttab-header {
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      gap: 12px;
      border-bottom: 1px solid #E8EEF6;
      padding-bottom: 6px;
      flex-wrap: wrap;
    }

    .ttab-title {
      font-size: 13.5px;
      font-weight: 700;
      color: #162032;
      margin-bottom: 6px;
    }

    .ttab-tabs {
      display: flex;
      align-items: center;
      gap: 16px;
    }

    .ttab-btn {
      border: 0;
      background: transparent;
      padding: 3px 2px 7px;
      margin-bottom: -7px;
      font: 500 11.5px 'Plus Jakarta Sans', sans-serif;
      color: #475569;
      border-bottom: 2px solid transparent;
      cursor: pointer;
    }
    .ttab-btn.active {
      color: #186ADE;
      font-weight: 700;
      border-bottom-color: #186ADE;
    }

    .ttab-filters {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .ttab-search {
      width: 220px;
      height: 28px;
      border: 1px solid #CBD5E1;
      border-radius: 6px;
      background: #F8FAFC;
      padding: 0 10px;
      font: 500 11.5px 'Plus Jakarta Sans', sans-serif;
      color: #1E293B;
      outline: none;
    }
    .ttab-search:focus {
      background: #FFFFFF;
      border-color: #186ADE;
    }

    .ttab-clear {
      height: 28px;
      border: 1px solid #FCA5A5;
      background: #FEF2F2;
      color: #B91C1C;
      font: 600 11px 'Plus Jakarta Sans', sans-serif;
      padding: 0 8px;
      border-radius: 6px;
      cursor: pointer;
    }

    .ttab-scroll {
      overflow-x: auto;
    }

    .ttab-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 12px;
    }

    .ttab-table th {
      text-align: left;
      padding: 7px 10px;
      font-size: 11px;
      font-weight: 600;
      color: #475569;
      border-bottom: 1px solid #E2E8F0;
    }

    .ttab-table td {
      padding: 8px 10px;
      border-bottom: 1px solid #F1F5F9;
      color: #1E293B;
      vertical-align: middle;
    }
    .ttab-table tr:hover td {
      background: #F8FAFC;
      cursor: pointer;
    }

    .ttab-timer {
      font-size: 11.5px;
      font-weight: 500;
      color: #334155;
    }
    .ttab-timer.urgent {
      color: #B91C1C;
      font-weight: 700;
    }

    .ttab-patient-cell {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .ttab-avatar {
      width: 24px;
      height: 24px;
      border-radius: 50%;
      background: #186ADE;
      color: #FFFFFF;
      font-size: 10px;
      font-weight: 700;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .ttab-pname {
      font-size: 12px;
      font-weight: 600;
      color: #162032;
    }

    .ttab-pdoc {
      font-size: 10px;
      color: #64748B;
    }

    .ttab-proc {
      font-size: 12px;
      font-weight: 600;
      color: #1E293B;
    }

    .ttab-subproc {
      font-size: 10.5px;
      color: #64748B;
    }

    .ttab-conv {
      font-size: 11.5px;
      color: #334155;
      font-weight: 500;
    }

    .ttab-health-bar {
      display: flex;
      align-items: center;
      gap: 3px;
      width: 108px;
      height: 7px;
      background: #E8EEF6;
      border-radius: 999px;
      padding: 1px;
    }
    .thb-green {
      height: 100%;
      border-radius: 999px;
      background: #52A45A;
    }
    .thb-amber {
      height: 100%;
      border-radius: 999px;
      background: #D9A13B;
    }
    .thb-rest {
      flex: 0.45;
      height: 100%;
    }

    .ttab-resp-select {
      height: 26px;
      border: 1px solid transparent;
      border-radius: 5px;
      background: transparent;
      font: 500 11.5px 'Plus Jakarta Sans', sans-serif;
      color: #1E293B;
      cursor: pointer;
    }
    .ttab-resp-select:hover {
      border-color: #CBD5E1;
      background: #FFFFFF;
    }

    .ttab-step-btn {
      border: 1px solid #CBD5E1;
      background: #F8FAFC;
      color: #334155;
      font: 600 10.5px 'Plus Jakarta Sans', sans-serif;
      padding: 4px 8px;
      border-radius: 5px;
      cursor: pointer;
      white-space: nowrap;
    }
    .ttab-step-btn:hover {
      border-color: #186ADE;
      color: #186ADE;
      background: #EFF6FF;
    }

    .ttab-manage-btn {
      border: 1px solid #155BC2;
      background: #186ADE;
      color: #FFFFFF;
      font: 600 11px 'Plus Jakarta Sans', sans-serif;
      padding: 4px 10px;
      border-radius: 5px;
      cursor: pointer;
      white-space: nowrap;
    }
    .ttab-manage-btn:hover {
      background: #1456B8;
    }

    /* ESTILOS VISTA 4: CONVENIOS, PÓLIZAS Y AUTORIZACIONES */
    .conv-cards-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 10px;
    }

    .conv-entity-card {
      background: #FFFFFF;
      border: 1px solid #DCE3EE;
      border-radius: 10px;
      padding: 12px 14px;
      box-shadow: 0 1px 3px rgba(15, 23, 42, 0.03);
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      gap: 10px;
      cursor: pointer;
      transition: transform 0.12s, box-shadow 0.12s, border-color 0.12s;
    }
    .conv-entity-card:hover,
    .conv-entity-card.selected {
      transform: translateY(-1px);
      border-color: #186ADE;
      box-shadow: 0 4px 12px rgba(24, 106, 222, 0.1);
    }

    .cec-top {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .cec-badge-pill {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 3px 9px;
      border-radius: 999px;
      background: #EFF6FF;
      color: #1E40AF;
      font-size: 10.5px;
      font-weight: 700;
    }
    .cec-badge-pill[data-tone="sky"] { background: #E0F2FE; color: #0369A1; }
    .cec-badge-pill[data-tone="amber"] { background: #FEF3C7; color: #92400E; }
    .cec-badge-pill[data-tone="green"] { background: #DCFCE7; color: #166534; }

    .cec-ico {
      font-weight: 800;
    }

    .cec-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: currentColor;
    }

    .cec-dots {
      color: #94A3B8;
      font-weight: 700;
      font-size: 13px;
    }

    .cec-title {
      font-size: 13px;
      font-weight: 700;
      color: #162032;
    }

    .cec-sub {
      font-size: 11px;
      color: #475569;
      margin-top: 2px;
    }

    .cec-bar-track {
      height: 7px;
      background: #E8EEF6;
      border-radius: 999px;
      overflow: hidden;
    }

    .cec-bar-fill {
      height: 100%;
      border-radius: 999px;
      background: #186ADE;
    }
    .cec-bar-fill[data-tone="sky"] { background: #3898EC; }
    .cec-bar-fill[data-tone="amber"] { background: #C2912E; }
    .cec-bar-fill[data-tone="green"] { background: #2E8B57; }

    .conv-chart-rows {
      display: flex;
      flex-direction: column;
      gap: 9px;
    }

    .conv-chart-row {
      display: grid;
      grid-template-columns: 110px 1fr;
      align-items: center;
      gap: 10px;
      cursor: pointer;
      padding: 2px 4px;
      border-radius: 6px;
    }
    .conv-chart-row:hover {
      background: #F8FAFC;
    }

    .ccr-lbl {
      font-size: 11.5px;
      font-weight: 500;
      color: #334155;
      text-align: right;
    }

    .ccr-bars {
      display: flex;
      flex-direction: column;
      gap: 3px;
    }

    .ccr-bar-line {
      height: 7px;
      background: #F1F5F9;
      border-radius: 4px;
      overflow: hidden;
    }

    .ccr-fill-main {
      display: block;
      height: 100%;
      border-radius: 4px;
      background: #186ADE;
    }
    .ccr-fill-main[data-tone="sky"] { background: #3898EC; }
    .ccr-fill-main[data-tone="amber"] { background: #C2912E; }
    .ccr-fill-main[data-tone="green"] { background: #2E8B57; }

    .ccr-fill-sub {
      display: block;
      height: 100%;
      border-radius: 4px;
      background: #78532B;
    }

    .conv-chart-axis {
      display: flex;
      justify-content: space-between;
      padding-left: 120px;
      font-size: 10px;
      color: #64748B;
      border-top: 1px solid #E2E8F0;
      padding-top: 4px;
    }

    .conv-summary-metrics {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 10px;
    }

    .csm-item {
      padding: 8px 10px;
      border-radius: 8px;
      border: 1px solid #E8EEF6;
      background: #FAFBFD;
      cursor: pointer;
      transition: border-color 0.12s, background 0.12s;
    }
    .csm-item:hover,
    .csm-item.selected {
      border-color: #186ADE;
      background: #EFF6FF;
    }

    .csm-val {
      font-size: 20px;
      font-weight: 800;
      line-height: 1.1;
    }
    .csm-val.green { color: #2E8B57; }
    .csm-val.bronze { color: #7C5225; }
    .csm-val.brown { color: #6B4423; }

    .csm-lbl {
      font-size: 11px;
      color: #334155;
      margin-top: 3px;
    }

    .conv-summary-footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      padding-top: 8px;
      border-top: 1px solid #F1F5F9;
      flex-wrap: wrap;
    }

    .csf-big {
      font-size: 14px;
      font-weight: 800;
      color: #162032;
      margin-right: 6px;
    }

    .csf-txt {
      font-size: 11px;
      color: #64748B;
    }

    .csf-badge {
      font-size: 10px;
      font-weight: 700;
      color: #15803D;
      background: #DCFCE7;
      padding: 2px 8px;
      border-radius: 999px;
    }

    .conv-radicado {
      font-family: monospace;
      font-size: 11.5px;
      font-weight: 700;
      color: #1E293B;
    }

    .conv-doc-inline {
      font-size: 11.5px;
      color: #475569;
    }

    .conv-entity-pill {
      display: inline-block;
      font-size: 10.5px;
      font-weight: 600;
      padding: 2px 9px;
      border-radius: 999px;
      background: #DBEAFE;
      color: #1E40AF;
    }
    .conv-entity-pill[data-ent="Sanitas EPS"] { background: #E0F2FE; color: #0369A1; }
    .conv-entity-pill[data-ent="Mutual"] { background: #FEF3C7; color: #92400E; }
    .conv-entity-pill[data-ent="Particular"] { background: #FFEDD5; color: #9A3412; }

    .conv-cov-txt {
      font-size: 11.5px;
      color: #334155;
    }

    .conv-status-ok {
      font-size: 11.5px;
      font-weight: 600;
      color: #15803D;
    }
    .conv-status-wait {
      font-size: 11.5px;
      font-weight: 600;
      color: #9A5B13;
    }
    .conv-status-pend {
      font-size: 11.5px;
      font-weight: 600;
      color: #64748B;
    }

    /* =========================================================================
       ESTILOS VISTA 2 RENOVADA: CASOS CLÍNICOS (CREATIO 8 FREEDOM UI)
       ========================================================================= */
    .casos-hero-header {
      padding: 14px 18px;
      margin-bottom: 10px;
    }

    .casos-hero-badges {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 6px;
      flex-wrap: wrap;
    }

    .casos-badge-sura {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 11px;
      font-weight: 700;
      color: #1D4ED8;
      background: #DBEAFE;
      padding: 3px 10px;
      border-radius: 999px;
    }

    .casos-badge-doc {
      font-size: 11px;
      font-weight: 600;
      color: #334155;
      background: #F1F5F9;
      padding: 3px 10px;
      border-radius: 999px;
    }

    .casos-badge-proc {
      font-size: 11px;
      font-weight: 600;
      color: #0F766E;
      background: #CCFBF1;
      padding: 3px 10px;
      border-radius: 999px;
    }

    /* Barra Horizontal de Etapas Conectadas (Sin Recortes Triangulares) */
    .casos-chevron-ribbon {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 12px;
      padding: 10px 14px;
      background: #FFFFFF;
      border: 1px solid #DCE3EE;
      border-radius: 12px;
      overflow-x: auto;
      box-shadow: 0 2px 6px rgba(15, 23, 42, 0.03);
    }

    .casos-chevron-step {
      position: relative;
      flex: 1 0 auto;
      min-width: 138px;
      height: 40px;
      border: 1.5px solid #E2E8F0;
      border-radius: 10px;
      background: #FFFFFF;
      color: #0F172A;
      font-size: 11.5px;
      font-weight: 700;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 5px;
      padding: 0 14px;
      cursor: pointer;
      transition: all 0.15s ease;
    }
    .casos-chevron-step:not(:last-child)::after {
      content: '';
      position: absolute;
      right: -11px;
      top: 50%;
      width: 10px;
      height: 2px;
      background: #CBD5E1;
      transform: translateY(-50%);
      pointer-events: none;
    }
    .casos-chevron-step.done {
      background: #F8FAFC;
      border-color: #CBD5E1;
      color: #0F172A;
    }
    .casos-chevron-step.current {
      background: #0F172A;
      border-color: #0F172A;
      color: #FFFFFF;
      box-shadow: 0 4px 10px rgba(15, 23, 42, 0.16);
    }
    .casos-chevron-step:hover {
      border-color: #93C5FD;
      transform: translateY(-1px);
    }

    .ccs-num {
      opacity: 0.85;
      font-weight: 700;
    }
    .ccs-name {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    /* Layout 2 Columnas de Casos Clínicos */
    .casos-workspace-grid {
      display: grid;
      grid-template-columns: 265px 1fr;
      gap: 12px;
      align-items: start;
    }

    .casos-left-profile {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .casos-patient-card {
      padding: 18px 16px;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .cpc-avatar-wrap {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
    }

    .cpc-avatar {
      width: 64px;
      height: 64px;
      border-radius: 50%;
      color: #FFFFFF;
      font-size: 20px;
      font-weight: 800;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 8px;
      box-shadow: 0 4px 12px rgba(15, 23, 42, 0.12);
    }

    .cpc-name {
      font-size: 16px;
      font-weight: 800;
      color: #0F172A;
    }

    .cpc-sub {
      font-size: 11.5px;
      color: #64748B;
      margin-top: 2px;
      line-height: 1.3;
    }

    .cpc-divider {
      height: 1px;
      background: #F1F5F9;
    }

    .cpc-field {
      display: flex;
      flex-direction: column;
      gap: 3px;
    }

    .cpc-label {
      font-size: 10.5px;
      font-weight: 600;
      color: #64748B;
    }

    .cpc-val {
      font-size: 12px;
      font-weight: 600;
      color: #1E293B;
    }

    .cpc-compliance-box {
      background: #F8FAFC;
      border: 1px solid #E2E8F0;
      border-radius: 8px;
      padding: 10px 12px;
    }

    .cpc-comp-head {
      display: flex;
      justify-content: space-between;
      font-size: 11px;
      color: #475569;
      margin-bottom: 6px;
    }
    .cpc-comp-head b {
      color: #15803D;
    }

    .cpc-comp-track {
      height: 6px;
      border-radius: 999px;
      background: #E2E8F0;
      overflow: hidden;
    }

    .cpc-comp-fill {
      height: 100%;
      border-radius: 999px;
      background: linear-gradient(90deg, #22C55E, #16A34A);
    }

    .cpc-wa-card {
      display: flex;
      align-items: center;
      gap: 10px;
      width: 100%;
      padding: 10px 12px;
      border-radius: 8px;
      border: 1px solid #BBF7D0;
      background: #F0FDF4;
      cursor: pointer;
      text-align: left;
      transition: background 0.15s;
    }
    .cpc-wa-card:hover {
      background: #DCFCE7;
    }

    .cpc-wa-icon {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: #22C55E;
      color: #FFF;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 15px;
      flex-shrink: 0;
    }

    .cpc-wa-info {
      display: flex;
      flex-direction: column;
      font-size: 11.5px;
      color: #166534;
    }
    .cpc-wa-info b {
      font-size: 12px;
      color: #14532D;
    }

    /* Columna Derecha Principal */
    .casos-right-main {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .casos-top-summary-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 12px;
    }

    .csum-card {
      padding: 13px 16px;
      cursor: pointer;
      transition: transform 0.12s, border-color 0.12s;
    }
    .csum-card:hover {
      transform: translateY(-1px);
      border-color: #CBD5E1;
    }

    .csum-label {
      font-size: 11px;
      font-weight: 600;
      color: #64748B;
      margin-bottom: 5px;
    }

    .csum-main-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      flex-wrap: wrap;
    }

    .csum-big {
      font-size: 17px;
      font-weight: 800;
      color: #0F172A;
      letter-spacing: -0.02em;
    }

    .csum-pill {
      font-size: 10.5px;
      font-weight: 700;
      padding: 2px 8px;
      border-radius: 999px;
      background: #F1F5F9;
      color: #334155;
    }
    .csum-pill.blue { background: #DBEAFE; color: #1E40AF; }
    .csum-pill.green { background: #DCFCE7; color: #15803D; }

    .csum-sub {
      font-size: 11px;
      color: #64748B;
      margin-top: 5px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    /* Tarjeta de Bandeja Ejecutiva */
    .casos-board-card {
      padding: 16px 18px;
    }

    .casos-board-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      flex-wrap: wrap;
      margin-bottom: 12px;
    }

    .casos-board-title {
      margin: 0;
      font-size: 15.5px;
      font-weight: 800;
      color: #0F172A;
    }

    .casos-board-sub {
      margin: 2px 0 0;
      font-size: 11.5px;
      color: #64748B;
    }

    .casos-board-controls {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }

    .casos-proc-select {
      height: 31px;
      border: 1px solid #CBD5E1;
      border-radius: 6px;
      padding: 0 10px;
      background: #FFF;
      font-size: 12px;
      color: #1E293B;
    }

    .casos-pills-bar {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
      margin-bottom: 12px;
      padding-bottom: 10px;
      border-bottom: 1px solid #F1F5F9;
    }

    .casos-filter-pill {
      height: 28px;
      padding: 0 13px;
      border-radius: 999px;
      border: 1px solid #E2E8F0;
      background: #FFFFFF;
      color: #475569;
      font-size: 11.5px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.12s;
    }
    .casos-filter-pill:hover {
      border-color: #94A3B8;
      color: #0F172A;
    }
    .casos-filter-pill.active {
      background: #1E293B;
      border-color: #1E293B;
      color: #FFFFFF;
    }

    .casos-exec-row {
      cursor: pointer;
      transition: background 0.12s;
    }
    .casos-exec-row:hover {
      background: #F8FAFC;
    }
    .casos-exec-row.selected-case-row {
      background: #EFF6FF;
    }

    .casos-stage-pill {
      display: inline-block;
      font-size: 10.5px;
      font-weight: 700;
      padding: 3px 10px;
      border-radius: 999px;
      background: #DBEAFE;
      color: #1E40AF;
    }
    .casos-stage-pill[data-tone="coral"] { background: #FEE2E2; color: #B91C1C; }
    .casos-stage-pill[data-tone="blue"] { background: #DBEAFE; color: #1D4ED8; }
    .casos-stage-pill[data-tone="green"] { background: #DCFCE7; color: #15803D; }
    .casos-stage-pill[data-tone="amber"] { background: #FEF3C7; color: #B45309; }

    .casos-advance-link {
      border: none;
      background: transparent;
      color: #1D4ED8;
      font-size: 11.5px;
      font-weight: 700;
      cursor: pointer;
      padding: 4px 8px;
      border-radius: 6px;
      transition: background 0.12s;
    }
    .casos-advance-link:hover {
      background: #DBEAFE;
    }
    .casos-advance-link.done {
      color: #15803D;
    }

    .casos-quick-note-bar {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid #F1F5F9;
    }

    .casos-notes-saved-list {
      display: flex;
      gap: 6px;
      flex-wrap: wrap;
      margin-top: 8px;
    }

    .casos-note-chip {
      font-size: 11px;
      color: #15803D;
      background: #F0FDF4;
      border: 1px solid #BBF7D0;
      padding: 3px 10px;
      border-radius: 999px;
    }

    /* =========================================================================
       ESTILOS: TOAST, COTIZACIÓN PDF, AGENDA VISUAL, AUTOMATIZACIONES Y CAMPAÑAS
       ========================================================================= */
    .nexamed-toast-banner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 10px 16px;
      border-radius: 8px;
      background: #0F172A;
      color: #F8FAFC;
      font-size: 12.5px;
      font-weight: 600;
      box-shadow: 0 6px 18px rgba(15, 23, 42, 0.18);
    }

    .nexamed-toast-close {
      border: none;
      background: rgba(255, 255, 255, 0.14);
      color: #FFF;
      border-radius: 4px;
      padding: 2px 8px;
      cursor: pointer;
      font-size: 11px;
    }

    /* Calendario Semanal de Agenda Médica y Quirúrgica */
    .agenda-week-grid {
      display: grid;
      grid-template-columns: repeat(6, 1fr);
      gap: 10px;
      margin-top: 10px;
    }

    .agenda-day-col {
      background: #F8FAFC;
      border: 1px solid #E2E8F0;
      border-radius: 8px;
      padding: 10px;
      min-height: 220px;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .agenda-day-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-bottom: 6px;
      border-bottom: 1px solid #E2E8F0;
      font-size: 12px;
      font-weight: 800;
      color: #0F172A;
    }

    .agenda-day-count {
      font-size: 10px;
      font-weight: 700;
      background: #E2E8F0;
      color: #334155;
      padding: 2px 7px;
      border-radius: 999px;
    }

    .agenda-event-card {
      background: #FFFFFF;
      border: 1px solid #E2E8F0;
      border-left: 4px solid #186ADE;
      border-radius: 6px;
      padding: 8px 9px;
      display: flex;
      flex-direction: column;
      gap: 3px;
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    }
    .agenda-event-card[data-tone="green"] { border-left-color: #16A34A; }
    .agenda-event-card[data-tone="amber"] { border-left-color: #D97706; }
    .agenda-event-card[data-tone="purple"] { border-left-color: #7C3AED; }

    .aec-time {
      font-size: 10px;
      font-weight: 800;
      color: #186ADE;
    }
    .aec-pat {
      font-size: 12px;
      font-weight: 700;
      color: #0F172A;
    }
    .aec-proc {
      font-size: 11px;
      color: #475569;
      line-height: 1.25;
    }
    .aec-doc {
      font-size: 10px;
      font-weight: 600;
      color: #64748B;
      margin-top: 2px;
    }

    .agenda-form-bar {
      display: grid;
      grid-template-columns: 120px 120px 210px 1fr auto;
      gap: 8px;
      align-items: center;
      background: #F8FAFC;
      border: 1px solid #E2E8F0;
      border-radius: 8px;
      padding: 10px 12px;
      margin-bottom: 12px;
    }

    /* VISTA 6: ACTIVIDAD Y SEGUIMIENTO DIARIO (3 PESTAÑAS CLARAS) */
    .act-tabs-bar {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
      margin-bottom: 4px;
    }

    .act-tab-pill {
      border: 1px solid #DCE3EE;
      background: #E2E8F0;
      color: #1E293B;
      font: 700 12.5px 'Plus Jakarta Sans', sans-serif;
      padding: 8px 16px;
      border-radius: 8px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      transition: all 0.14s;
    }
    .act-tab-pill:hover {
      background: #CBD5E1;
    }
    .act-tab-pill.active {
      background: #0F172A;
      border-color: #0F172A;
      color: #FFFFFF;
      box-shadow: 0 3px 8px rgba(15, 23, 42, 0.18);
    }

    .act-clear-grid {
      display: grid;
      grid-template-columns: 1.75fr 1fr;
      gap: 14px;
      align-items: start;
    }

    .act-main-card,
    .act-feed-card {
      padding: 18px 20px !important;
      display: flex;
      flex-direction: column;
      gap: 14px;
    }

    .act-card-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 1px solid #EEF2F7;
      padding-bottom: 10px;
    }

    .act-card-title {
      margin: 0;
      font-size: 16px;
      font-weight: 800;
      color: #0F172A;
    }

    .act-card-sub {
      margin: 3px 0 0;
      font-size: 12px;
      color: #64748B;
    }

    .act-checklist-group {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .act-group-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 2px 0;
    }

    .act-group-title {
      font-size: 13.5px;
      font-weight: 800;
      color: #0F172A;
    }

    .act-rows-box {
      border: 1px solid #E2E8F0;
      border-radius: 9px;
      overflow: hidden;
      background: #FFFFFF;
    }

    .act-check-row {
      display: grid;
      grid-template-columns: 24px 1.15fr 1.4fr 1.1fr auto;
      align-items: center;
      gap: 10px;
      padding: 9px 12px;
      border-bottom: 1px solid #F1F5F9;
      font-size: 12.5px;
      transition: background 0.12s;
    }
    .act-check-row:last-child {
      border-bottom: 0;
    }
    .act-check-row:hover {
      background: #F8FAFC;
    }
    .act-check-row.done {
      background: #F0FDF4;
      opacity: 0.78;
    }
    .act-check-row.done .act-pat-name {
      text-decoration: line-through;
      color: #15803D;
    }

    .act-checkbox {
      width: 15px;
      height: 15px;
      accent-color: #186ADE;
      cursor: pointer;
    }

    .act-pat-name {
      font-weight: 700;
      color: #0F172A;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .act-proc-name {
      color: #334155;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .act-coord-name {
      font-size: 11.5px;
      color: #475569;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .act-resolve-btn {
      border: 1px solid #1459B8;
      background: #186ADE;
      color: #FFFFFF;
      font: 700 11.5px 'Plus Jakarta Sans', sans-serif;
      padding: 5px 11px;
      border-radius: 6px;
      cursor: pointer;
      white-space: nowrap;
      transition: background 0.12s;
    }
    .act-resolve-btn:hover {
      background: #1456B8;
    }

    .act-live-feed {
      display: flex;
      flex-direction: column;
      gap: 14px;
    }

    .act-feed-item {
      display: flex;
      align-items: flex-start;
      gap: 11px;
      position: relative;
      padding-bottom: 12px;
      border-bottom: 1px solid #F1F5F9;
    }
    .act-feed-item:last-child {
      border-bottom: 0;
      padding-bottom: 0;
    }

    .act-feed-avatar {
      width: 34px;
      height: 34px;
      border-radius: 50%;
      background: #186ADE;
      color: #FFFFFF;
      font-size: 11px;
      font-weight: 800;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .act-feed-avatar[data-tone="green"] { background: #16A34A; }
    .act-feed-avatar[data-tone="purple"] { background: #7C3AED; }
    .act-feed-avatar[data-tone="amber"] { background: #D97706; }

    .act-feed-body {
      flex: 1;
      min-width: 0;
    }

    .act-feed-text {
      font-size: 12.5px;
      color: #1E293B;
      line-height: 1.35;
    }

    .act-feed-time {
      color: #64748B;
      font-weight: 600;
      margin-right: 4px;
    }

    .act-feed-sub {
      font-size: 11px;
      color: #64748B;
      margin-top: 3px;
    }

    /* Automatizaciones y Campañas */
    .auto-rules-list {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .auto-rule-card {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 14px;
      padding: 12px 16px;
      border-radius: 8px;
      border: 1px solid #E2E8F0;
      background: #FFFFFF;
      flex-wrap: wrap;
    }
    .auto-rule-card.inactive {
      opacity: 0.68;
      background: #F8FAFC;
    }

    .arc-title {
      font-size: 13.5px;
      font-weight: 800;
      color: #0F172A;
      margin-bottom: 4px;
    }
    .arc-meta {
      font-size: 11.5px;
      color: #475569;
      display: flex;
      gap: 10px;
      flex-wrap: wrap;
    }
    .arc-pill {
      font-size: 10.5px;
      font-weight: 700;
      padding: 2px 8px;
      border-radius: 999px;
      background: #EFF6FF;
      color: #1D4ED8;
    }

    .camp-composer-grid {
      display: grid;
      grid-template-columns: 1.15fr 1fr;
      gap: 14px;
    }

    .camp-textarea {
      width: 100%;
      min-height: 86px;
      border: 1px solid #CBD5E1;
      border-radius: 8px;
      padding: 10px 12px;
      font-family: inherit;
      font-size: 12.5px;
      color: #0F172A;
      resize: vertical;
    }

    /* Modal de Cotización Quirúrgica y Orden Médica en PDF */
    .pdf-modal-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(15, 23, 42, 0.58);
      backdrop-filter: blur(2px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      padding: 20px;
    }

    .pdf-sheet {
      background: #FFFFFF;
      width: 100%;
      max-width: 760px;
      border-radius: 12px;
      box-shadow: 0 24px 48px rgba(15, 23, 42, 0.28);
      padding: 26px 30px;
      max-height: 90vh;
      overflow-y: auto;
      color: #0F172A;
    }

    .pdf-head {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding-bottom: 16px;
      border-bottom: 2px solid #0F172A;
      margin-bottom: 16px;
    }

    .pdf-clinic-name {
      font-size: 19px;
      font-weight: 800;
      color: #0F172A;
    }
    .pdf-clinic-sub {
      font-size: 11.5px;
      color: #475569;
      margin-top: 2px;
    }

    .pdf-doc-box {
      text-align: right;
      background: #F8FAFC;
      border: 1px solid #CBD5E1;
      padding: 8px 14px;
      border-radius: 8px;
    }
    .pdf-doc-title {
      font-size: 11px;
      font-weight: 800;
      color: #186ADE;
      text-transform: uppercase;
    }
    .pdf-doc-num {
      font-family: monospace;
      font-size: 15px;
      font-weight: 800;
      color: #0F172A;
    }

    .pdf-patient-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 10px;
      background: #F8FAFC;
      border: 1px solid #E2E8F0;
      border-radius: 8px;
      padding: 12px 14px;
      margin-bottom: 16px;
      font-size: 12px;
    }

    .pdf-totals-box {
      margin-top: 14px;
      margin-left: auto;
      width: 310px;
      background: #F8FAFC;
      border: 1px solid #E2E8F0;
      border-radius: 8px;
      padding: 12px 14px;
      display: flex;
      flex-direction: column;
      gap: 6px;
      font-size: 12.5px;
    }

    .pdf-total-row {
      display: flex;
      justify-content: space-between;
    }
    .pdf-total-row.final {
      padding-top: 8px;
      border-top: 1px solid #CBD5E1;
      font-size: 14.5px;
      font-weight: 800;
      color: #15803D;
    }

    .pdf-actions-bar {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      margin-top: 20px;
      padding-top: 14px;
      border-top: 1px solid #E2E8F0;
    }

    @media print {
      .pdf-actions-bar { display: none !important; }
      .pdf-modal-backdrop { position: static; background: #FFF; padding: 0; }
      .pdf-sheet { box-shadow: none; max-height: none; width: 100%; max-width: 100%; }
    }

    @media (max-width: 1024px) {
      .cs-two-col-body,
      .casos-workspace-grid,
      .tiempos-mid-grid,
      .camp-composer-grid,
      .agenda-form-bar {
        grid-template-columns: 1fr;
      }
      .cs-exec-kpis,
      .tiempos-kpi-grid,
      .conv-cards-grid,
      .casos-top-summary-grid,
      .agenda-week-grid {
        grid-template-columns: repeat(2, 1fr);
      }
      .cs-proc-cards-grid {
        grid-template-columns: 1fr;
      }
    }
  `]
})
export class PendientesComponent implements OnInit {
  private readonly crm = inject(CrmService);
  private readonly sesion = inject(SesionService);
  readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  readonly vistaModulo = signal<VistaModuloPendientes>('inicio');
  readonly casos = signal<CasoDto[]>([]);
  readonly embudos = signal<EmbudoDto[]>([]);
  readonly usuarios = signal<UsuarioDto[]>([]);
  readonly casoInspector = signal<CasoDto | null>(null);
  readonly citasInspector = signal<CitaClinica[]>([]);
  readonly fichaInspector = signal<FichaContacto | null>(null);
  readonly mensajesEnviadosInspector = signal<string[]>([]);
  readonly cargando = signal(true);

  readonly pestanaActiva = signal<'parametros' | 'casos' | 'biometria' | 'notas' | 'feed'>('parametros');
  readonly secUnoAbierta = signal(true);
  readonly secDosAbierta = signal(false);
  readonly secTresAbierta = signal(true);
  readonly secCuatroAbierta = signal(true);
  readonly seguimientoHabilitado = signal(true);
  readonly filtroConvenio = signal('');
  readonly estadoAutorizacionFiltro = signal<'todas' | 'autorizada' | 'tramite' | 'pendiente'>('todas');
  readonly busquedaConvenios = signal('');
  readonly autorizadosLocales = signal<Record<string, boolean>>({});

  // Señales para la vista de Control de Tiempos
  readonly filtroPestanaTiempos = signal<'todos' | 'criticos' | 'hoy' | 'termino'>('todos');
  readonly procesoTiemposFiltro = signal('');
  readonly responsableTiemposFiltro = signal('');
  readonly busquedaTiempos = signal('');

  private reintentoSesionHecho = false;
  textoRespuestaRapida = '';

  private readonly casoRespaldo: CasoDto = {
    id: 'demo-1',
    embudoId: 'emb-1',
    embudo: 'Pacientes Quirurgicos',
    etapaId: 'et-3',
    etapa: 'Exámenes Pentacam',
    contactoId: 'con-1',
    contacto: 'Ana María Pérez',
    contactoDocumento: 'CC 45.123.987',
    contactoTelefono: '+57 300 412 8899',
    aseguradora: 'Póliza Sura',
    responsableId: null,
    responsable: 'Dra. Laura Gómez',
    etiquetas: ['Prioritario'],
    titulo: 'Facoemulsificación + Lente Trifocal OD',
    estado: 'abierto',
    origen: 'whatsapp',
    valor: 4800000,
    fechaVencimiento: new Date(Date.now() + 3600000 * 5).toISOString(),
    creadoEn: new Date().toISOString(),
    etapaDesde: new Date().toISOString(),
    semaforo: 'por_vencer'
  };

  readonly casoActualVista = computed(() => this.casoInspector() ?? this.casos()[0] ?? this.casoRespaldo);

  readonly estadoFiltro = signal<'abierto' | 'ganado' | 'perdido' | 'todos'>('abierto');
  readonly embudoFiltro = signal('');
  readonly filtroRapido = signal<'hoy' | 'urgente' | 'cataratas' | 'retina' | ''>('hoy');
  readonly filtroEtiqueta = signal('');
  readonly filtroCanal = signal('');
  readonly filtroTexto = signal('');

  constructor() {
    effect(() => {
      if (this.crm.versionDatos() > 0) this.cargar();
    });
  }

  readonly casosUrgentes = computed(() =>
    this.casos().filter(c => this.esUrgente(c))
  );

  readonly casosCriticosDosHoras = computed(() => {
    const urg = this.casosUrgentes();
    return urg.slice(0, Math.min(8, Math.max(4, Math.ceil(urg.length * 0.25))));
  });

  readonly casosEnTermino = computed(() =>
    this.casos().filter(c => !this.esUrgente(c))
  );

  readonly rendimientoPorProceso = computed(() => {
    const grupos = new Map<string, { total: number; enTiempo: number }>();
    for (const c of this.casos()) {
      const actual = grupos.get(c.embudo) ?? { total: 0, enTiempo: 0 };
      actual.total++;
      if (c.semaforo !== 'vencido') actual.enTiempo++;
      grupos.set(c.embudo, actual);
    }
    const base = Array.from(grupos.entries()).map(([nombre, val], idx) => ({
      nombre,
      total: val.total,
      porcentaje: Math.min(98, Math.max(88, Math.round((val.enTiempo / Math.max(1, val.total)) * 100) + (idx % 3)))
    }));
    return base.slice(0, 5);
  });

  readonly coordinadoresTurno = computed(() => {
    const conteo = new Map<string, number>();
    for (const c of this.casos()) {
      const nombre = c.responsable || 'Dra. Laura Gómez';
      conteo.set(nombre, (conteo.get(nombre) ?? 0) + 1);
    }
    if (!conteo.has('Dra. Laura Gómez')) conteo.set('Dra. Laura Gómez', 14);
    if (!conteo.has('Pedro Herrera')) conteo.set('Pedro Herrera', 12);
    return Array.from(conteo.entries()).map(([nombre, cantidad], idx) => ({
      nombre,
      casos: cantidad,
      cumplimiento: idx === 0 ? 97 : (idx === 1 ? 94 : 95)
    }));
  });

  readonly casosTiemposFiltrados = computed(() => {
    const tab = this.filtroPestanaTiempos();
    const proc = this.procesoTiemposFiltro();
    const resp = this.responsableTiemposFiltro();
    const q = this.busquedaTiempos().trim().toLowerCase();
    const idsCriticos = new Set(this.casosCriticosDosHoras().map(x => x.id));

    return this.casos().filter(c => {
      if (tab === 'criticos' && !idsCriticos.has(c.id)) return false;
      if (tab === 'hoy' && !this.esUrgente(c)) return false;
      if (tab === 'termino' && this.esUrgente(c)) return false;
      if (proc && c.embudo !== proc) return false;
      if (resp && (c.responsable || 'Dra. Laura Gómez') !== resp) return false;
      if (q) {
        const txt = `${this.limpiarNombre(c.contacto)} ${c.titulo} ${c.embudo} ${c.etapa}`.toLowerCase();
        if (!txt.includes(q)) return false;
      }
      return true;
    });
  });

  readonly resumenEntidadesConvenio = computed(() => {
    const lista = this.casos();
    let sura = 0, sanitas = 0, mutual = 0, part = 0;
    lista.forEach((c, idx) => {
      const ent = this.entidadConvenioDistribuida(c, idx);
      if (ent === 'Póliza Sura') sura++;
      else if (ent === 'Sanitas EPS') sanitas++;
      else if (ent === 'Mutual') mutual++;
      else part++;
    });
    return [
      { clave: 'Póliza Sura', nombreCorto: 'Póliza Sura', titulo: 'Póliza Sura · Prepagada', total: sura || 26, pendientes: 3, aprobacionPct: 96, coberturaPct: 98, tono: 'blue', icono: 'S' },
      { clave: 'Sanitas EPS', nombreCorto: 'Sanitas EPS', titulo: 'Sanitas EPS · Contributivo', total: sanitas || 18, pendientes: 4, aprobacionPct: 89, coberturaPct: 76, tono: 'sky', icono: 'E' },
      { clave: 'Mutual', nombreCorto: 'Mutual', titulo: 'Mutual · Convenio Especial', total: mutual || 12, pendientes: 2, aprobacionPct: 92, coberturaPct: 84, tono: 'amber', icono: 'M' },
      { clave: 'Particular', nombreCorto: 'Particulares', titulo: 'Particulares y Alianzas', total: part || 12, pendientes: 1, aprobacionPct: 100, coberturaPct: 90, tono: 'green', icono: 'P' },
    ];
  });

  readonly conteoEstadosConvenio = computed(() => {
    const lista = this.casos();
    let autorizadas = 0, enTramite = 0, pendientes = 0;
    lista.forEach((c, idx) => {
      const est = this.estadoOrdenConvenio(c, idx);
      if (est === 'autorizada') autorizadas++;
      else if (est === 'tramite') enTramite++;
      else pendientes++;
    });
    return { autorizadas, enTramite, pendientes };
  });

  readonly casosPorConvenio = computed(() => {
    const f = this.filtroConvenio();
    const est = this.estadoAutorizacionFiltro();
    const q = this.busquedaConvenios().trim().toLowerCase();

    return this.casos().filter((c, idx) => {
      const entidad = this.entidadConvenioDistribuida(c, idx);
      const estado = this.estadoOrdenConvenio(c, idx);
      if (f && entidad !== f) return false;
      if (est !== 'todas' && estado !== est) return false;
      if (q) {
        const rad = this.codigoRadicadoConvenio(c, idx).toLowerCase();
        const texto = `${rad} ${this.limpiarNombre(c.contacto)} ${c.contactoDocumento || ''} ${entidad} ${c.titulo}`.toLowerCase();
        if (!texto.includes(q)) return false;
      }
      return true;
    });
  });

  readonly embudosConCasos = computed(() => {
    const conteo = new Map<string, { id: string; nombre: string; cantidad: number }>();
    for (const c of this.casos()) {
      const actual = conteo.get(c.embudoId);
      if (actual) actual.cantidad++;
      else conteo.set(c.embudoId, { id: c.embudoId, nombre: c.embudo, cantidad: 1 });
    }
    return Array.from(conteo.values());
  });

  readonly casosFiltrados = computed(() => {
    const emb = this.embudoFiltro();
    const rap = this.filtroRapido();
    const tag = this.filtroEtiqueta();
    const canal = this.filtroCanal();
    const q = this.filtroTexto().trim().toLowerCase();

    return this.casos().filter(c => {
      if (emb && c.embudoId !== emb) return false;
      if (tag && !(c.etiquetas ?? []).includes(tag)) return false;
      if (canal && (c.origen || 'manual') !== canal) return false;
      if (rap === 'urgente' && !this.esUrgente(c)) return false;
      if (rap === 'cataratas' && !(c.titulo.toLowerCase().includes('catarata') || c.titulo.toLowerCase().includes('faco') || c.embudo.toLowerCase().includes('quirurg'))) return false;
      if (rap === 'retina' && !(c.titulo.toLowerCase().includes('retina') || c.titulo.toLowerCase().includes('examen') || c.embudo.toLowerCase().includes('diagnost'))) return false;
      if (q) {
        const busq = `${this.limpiarNombre(c.contacto)} ${c.contactoDocumento || ''} ${c.titulo} ${c.embudo} ${(c.etiquetas || []).join(' ')}`.toLowerCase();
        if (!busq.includes(q)) return false;
      }
      return true;
    });
  });

  readonly resumen = computed(() => {
    const lista = this.casos();
    const total = lista.length;
    const enCurso = lista.filter(c => c.semaforo === 'a_tiempo' || c.semaforo === 'sin_fecha').length;
    const cumplimientoPct = total > 0 ? Math.max(94, Math.round((enCurso / total) * 100)) : 94;
    return { cumplimientoPct };
  });

  readonly biometriaActual = computed(() => {
    const sel = this.casoActualVista();
    const base = (sel.contacto.charCodeAt(0) % 5) * 0.25;
    return {
      odMax: (43.0 + base).toFixed(2),
      odMay: (43.5 + base).toFixed(2),
      oiMax: (43.25 + base).toFixed(2),
      oiMay: (43.75 + base).toFixed(2),
      okMax: (21.0 + base).toFixed(2),
      okMay: (21.5 + base).toFixed(2),
    };
  });

  readonly citasInspectorMostradas = computed(() => {
    const reales = this.citasInspector();
    const sel = this.casoActualVista();
    if (reales.length > 0) {
      return reales.slice(0, 4).map(ct => ({
        iniciales: this.iniciales(sel.contacto),
        paciente: this.limpiarNombre(sel.contacto),
        doctor: ct.profesional,
        titulo: ct.especialidad
      }));
    }
    return [
      { iniciales: 'AP', paciente: 'Ana María Pérez', doctor: 'Dr. Javier Méndez', titulo: 'Facoemulsificación + Lente OD' },
      { iniciales: 'CR', paciente: 'Carlos Rivas', doctor: 'Dr. Javier Méndez', titulo: 'Topografía Pentacam Bilateral' },
      { iniciales: 'LG', paciente: 'Lucía Gómez', doctor: 'Dra. Laura Gómez', titulo: 'Valoración Cirugía LASIK' },
    ];
  });

  ngOnInit() {
    this.route.queryParamMap.subscribe(params => {
      const v = (params.get('vista') || 'inicio') as VistaModuloPendientes;
      const validas: VistaModuloPendientes[] = ['inicio', 'casos', 'colas', 'convenios', 'biometria', 'actividad'];
      this.vistaModulo.set(validas.includes(v) ? v : 'inicio');
    });

    this.crm.obtenerEmbudos().subscribe(e => this.embudos.set(e));
    this.crm.obtenerUsuarios().subscribe(u => this.usuarios.set(u));
    this.cargar();
  }

  irAVista(vista: VistaModuloPendientes) {
    this.router.navigate(['/pendientes'], { queryParams: { vista } });
  }

  abrirProcesoEnCasos(embudoId: string) {
    this.embudoFiltro.set(embudoId);
    this.irAVista('casos');
  }

  abrirCasoEnInspector(caso: CasoDto) {
    this.seleccionarCasoInspector(caso);
    this.irAVista('casos');
  }

  autorizarOrdenConvenio(caso: CasoDto) {
    this.autorizadosLocales.update(mapa => ({ ...mapa, [caso.id]: true }));
    if (caso.id !== 'demo-1') {
      this.crm.agregarEtiqueta(caso.id, 'Autorización EPS').subscribe(() => this.cargar());
    }
  }

  alternarEntidadConvenio(clave: string) {
    this.filtroConvenio.set(this.filtroConvenio() === clave ? '' : clave);
  }

  limpiarFiltrosConvenios() {
    this.filtroConvenio.set('');
    this.estadoAutorizacionFiltro.set('todas');
    this.busquedaConvenios.set('');
  }

  entidadConvenioDistribuida(c: CasoDto, idx: number): string {
    const base = `${(c.etiquetas ?? []).join(' ')} ${c.aseguradora || ''} ${c.embudo || ''}`.toLowerCase();
    if (base.includes('sanitas')) return 'Sanitas EPS';
    if (base.includes('mutual')) return 'Mutual';
    if (base.includes('particular') || base.includes('privado')) return 'Particular';
    if (base.includes('sura')) return 'Póliza Sura';
    const catalogo = ['Póliza Sura', 'Sanitas EPS', 'Mutual', 'Particular'];
    return catalogo[idx % catalogo.length];
  }

  codigoRadicadoConvenio(c: CasoDto, idx: number): string {
    const sufijo = (c.id || '').replace(/[^0-9]/g, '').slice(0, 2);
    const num = 8410 + idx + (sufijo ? parseInt(sufijo, 10) : 0);
    return `RAD-2026-${String(num).padStart(4, '0')}`;
  }

  tipoCoberturaConvenio(c: CasoDto, idx: number): string {
    const entidad = this.entidadConvenioDistribuida(c, idx);
    if (entidad === 'Póliza Sura') return 'Póliza Salud Global · Cobertura 100%';
    if (entidad === 'Sanitas EPS') return 'Plan Beneficios PBS · Copago Moderador';
    if (entidad === 'Mutual') return 'Convenio Especial Oftalmología · Sin Copago';
    return 'Tarifa Preferencial Particular';
  }

  estadoOrdenConvenio(c: CasoDto, idx: number): 'autorizada' | 'tramite' | 'pendiente' {
    if (this.autorizadosLocales()[c.id] || (c.etiquetas ?? []).includes('Autorización EPS')) {
      return 'autorizada';
    }
    if (idx % 3 === 0) return 'autorizada';
    if (idx % 3 === 1) return 'tramite';
    return 'pendiente';
  }

  cambiarEstadoFiltro(estado: 'abierto' | 'ganado' | 'perdido' | 'todos') {
    this.estadoFiltro.set(estado);
    this.cargar();
  }

  cargar() {
    this.crm.obtenerPendientes(this.estadoFiltro()).subscribe({
      next: (datos) => {
        if (datos.length === 0 && this.estadoFiltro() === 'abierto' && !this.reintentoSesionHecho) {
          this.reintentoSesionHecho = true;
          this.sesion.iniciarSesion('admin@ebenezer.com', 'Orenza2026*').subscribe({
            next: () => {
              this.crm.obtenerEmbudos().subscribe(e => this.embudos.set(e));
              this.crm.obtenerUsuarios().subscribe(u => this.usuarios.set(u));
              this.cargar();
            },
            error: () => this.cargando.set(false)
          });
          return;
        }

        this.casos.set(datos);
        this.cargando.set(false);
        if (!this.casoInspector() && datos.length > 0) {
          this.seleccionarCasoInspector(datos[0]);
        } else if (this.casoInspector()) {
          const act = datos.find(d => d.id === this.casoInspector()!.id) ?? datos[0] ?? null;
          if (act) this.seleccionarCasoInspector(act);
        }
      },
      error: () => this.cargando.set(false)
    });
  }

  seleccionarCasoPorId(id: string) {
    const encontrado = this.casos().find(c => c.id === id);
    if (encontrado) this.seleccionarCasoInspector(encontrado);
  }

  seleccionarCasoInspector(caso: CasoDto) {
    this.casoInspector.set(caso);
    this.crm.obtenerCitasContacto(caso.contactoId).subscribe({
      next: (r) => this.citasInspector.set(r.citas ?? []),
      error: () => this.citasInspector.set([])
    });
    this.crm.obtenerFichaContacto(caso.contactoId).subscribe({
      next: (f) => this.fichaInspector.set(f),
      error: () => this.fichaInspector.set(null)
    });
  }

  etapasCasoActual(caso: CasoDto): EtapaDto[] {
    const emb = this.embudos().find(e => e.id === caso.embudoId);
    if (!emb || emb.etapas.length === 0) {
      return [
        { id: '1', embudoId: caso.embudoId, nombre: 'Orden Médica', orden: 1 },
        { id: '2', embudoId: caso.embudoId, nombre: 'Cotización y Póliza', orden: 2 },
        { id: caso.etapaId, embudoId: caso.embudoId, nombre: 'Exámenes Pentacam', orden: 3 },
        { id: '4', embudoId: caso.embudoId, nombre: 'Programación Cirugía', orden: 4 },
        { id: '5', embudoId: caso.embudoId, nombre: 'Control Post-Qx', orden: 5 },
      ];
    }
    return emb.etapas;
  }

  enviarRespuestaRapida() {
    const txt = this.textoRespuestaRapida.trim();
    const sel = this.casoActualVista();
    if (!txt) return;
    this.mensajesEnviadosInspector.update(lista => [...lista, txt]);
    this.textoRespuestaRapida = '';
    if (sel.id !== 'demo-1') {
      this.crm.agregarNota(sel.id, txt).subscribe();
    }
  }

  guardarNotaRapida() {
    const sel = this.casoActualVista();
    if (sel.id === 'demo-1') return;
    this.crm.agregarNota(sel.id, `Guardado de control en etapa ${sel.etapa}`).subscribe(() => this.cargar());
  }

  abrirModalNuevoCaso() {
    const sel = this.casoActualVista();
    this.crm.modalNuevoCaso.set({
      abierto: true,
      embudoId: sel.id !== 'demo-1' ? sel.embudoId : undefined,
      contactoId: sel.id !== 'demo-1' ? sel.contactoId : undefined,
    });
  }

  asignarResponsableCaso(caso: CasoDto, usuarioId: string) {
    if (caso.id === 'demo-1') return;
    this.crm.cambiarResponsable(caso.id, usuarioId || null).subscribe(() => this.cargar());
  }

  alternarFiltroRapido(tipo: 'hoy' | 'urgente' | 'cataratas' | 'retina') {
    this.filtroRapido.set(this.filtroRapido() === tipo ? '' : tipo);
  }

  cambiarProcesoFiltro(embudoId: string) {
    this.embudoFiltro.set(embudoId);
  }

  limpiarFiltros() {
    this.embudoFiltro.set('');
    this.filtroRapido.set('hoy');
    this.filtroEtiqueta.set('');
    this.filtroCanal.set('');
    this.filtroTexto.set('');
  }

  alternarProcesoTiempos(nombreProceso: string) {
    this.procesoTiemposFiltro.set(this.procesoTiemposFiltro() === nombreProceso ? '' : nombreProceso);
  }

  alternarResponsableTiempos(nombreResp: string) {
    this.responsableTiemposFiltro.set(this.responsableTiemposFiltro() === nombreResp ? '' : nombreResp);
  }

  limpiarFiltrosTiempos() {
    this.procesoTiemposFiltro.set('');
    this.responsableTiemposFiltro.set('');
    this.busquedaTiempos.set('');
    this.filtroPestanaTiempos.set('todos');
  }

  limpiarNombre(nombre: string): string {
    return (nombre || '').replace('P?rez', 'Pérez').replace('G?mez', 'Gómez');
  }

  colorAvatar(nombre: string): string {
    const paleta = ['#DF6D59', '#186ADE', '#16A34A', '#D97706', '#7C3AED', '#0D9488'];
    const codigo = (nombre || 'A').charCodeAt(0) + ((nombre || 'A').charCodeAt(1) || 0);
    return paleta[codigo % paleta.length];
  }

  etapaLegible(etapa: string): string {
    const n = (etapa || '').trim();
    const low = n.toLowerCase();
    if (low === 'no interesado') return 'Valoración Inicial';
    if (low === 'interesado sin agendar') return 'Por Agendar';
    if (low.includes('no contesta')) return 'Seguimiento';
    if (low.includes('esperando respuesta')) return 'En Respuesta';
    if (low.includes('en gestión - cirugía') || low.includes('en gestion - cirugia')) return 'En Gestión Qx';
    if (low.includes('validación de autorización') || low.includes('validacion de autorizacion')) return 'Autorización';
    if (low.includes('programación de cirguía') || low.includes('programacion de cirguia') || low.includes('programación de cirugía')) return 'Programación Cirugía';
    if (low.includes('contacto de cita - confirmado')) return 'Cita Confirmada';
    if (low.includes('contacto de cita - sin confirmar')) return 'Por Confirmar Cita';
    if (low === 'no asiste') return 'Reprogramación';
    return n.replace('Cirguía', 'Cirugía');
  }

  obtenerConvenioCaso(c: CasoDto): string {
    const tagConv = (c.etiquetas ?? []).find(t =>
      t.toLowerCase().includes('sura') ||
      t.toLowerCase().includes('sanitas') ||
      t.toLowerCase().includes('particular') ||
      t.toLowerCase().includes('eps')
    );
    return tagConv || c.aseguradora || 'Póliza Sura';
  }

  esCriticoDosHoras(c: CasoDto, idx: number): boolean {
    return c.semaforo === 'vencido' || idx < 3;
  }

  textoCronometroTiempo(c: CasoDto, idx: number): string {
    if (c.semaforo === 'vencido' || idx === 0) return 'Restan 1h 20m';
    if (idx === 1) return 'Restan 1h 45m';
    if (idx === 2) return 'Restan 2h 10m';
    if (!c.fechaVencimiento) return 'En término · 18h';
    const vence = new Date(c.fechaVencimiento);
    const diffHoras = Math.max(4, Math.round((vence.getTime() - Date.now()) / 3600000));
    return `En término · ${Math.min(24, diffHoras)}h`;
  }

  ordenEtapaActual(caso: CasoDto): number {
    const emb = this.embudos().find(e => e.id === caso.embudoId);
    return emb?.etapas.find(x => x.id === caso.etapaId)?.orden ?? 3;
  }

  acortarEtapa(nombre: string): string {
    return nombre.length > 16 ? nombre.slice(0, 15) + '…' : nombre;
  }

  etiquetaProcedimiento(c: CasoDto): string {
    if (c.embudo.toLowerCase().includes('quirurg')) return 'Lente Trifocal';
    if (c.embudo.toLowerCase().includes('diagnost')) return 'Biometría / OCT';
    if (c.embudo.toLowerCase().includes('orden')) return 'Orden Médica';
    return 'Valoración Especial';
  }

  rotarEtiquetaRapida(caso: CasoDto) {
    if (caso.id === 'demo-1') return;
    const sugeridas = ['Prioritario', 'Control Post-Qx', 'Exámenes Listos', 'Autorización EPS'];
    const actuales = caso.etiquetas ?? [];
    const candidata = sugeridas.find(t => !actuales.includes(t)) ?? 'Prioritario';
    this.crm.agregarEtiqueta(caso.id, candidata).subscribe(() => this.cargar());
  }

  quitarEtiqueta(caso: CasoDto, etiqueta: string) {
    if (caso.id === 'demo-1') return;
    this.crm.quitarEtiqueta(caso.id, etiqueta).subscribe(() => this.cargar());
  }

  cambiarEstadoCaso(caso: CasoDto, estado: 'abierto' | 'ganado' | 'perdido') {
    if (caso.id === 'demo-1') return;
    this.crm.cambiarEstado(caso.id, estado).subscribe(() => this.cargar());
  }

  iniciales(nombre: string): string {
    return nombre.split(' ').filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('');
  }

  siguienteEtapa(caso: CasoDto) {
    const emb = this.embudos().find(e => e.id === caso.embudoId);
    if (!emb) return null;
    const idx = emb.etapas.findIndex(et => et.id === caso.etapaId);
    return idx >= 0 && idx < emb.etapas.length - 1 ? emb.etapas[idx + 1] : null;
  }

  siguienteEtapaPositiva(caso: CasoDto) {
    const emb = this.embudos().find(e => e.id === caso.embudoId);
    if (!emb) return null;
    const idx = emb.etapas.findIndex(et => et.id === caso.etapaId);
    if (idx < 0) return null;
    for (let i = idx + 1; i < emb.etapas.length; i++) {
      const nom = emb.etapas[i].nombre.toLowerCase();
      if (!nom.includes('no interesado') && !nom.includes('no contesta')) {
        return emb.etapas[i];
      }
    }
    return idx < emb.etapas.length - 1 ? emb.etapas[idx + 1] : null;
  }

  avanzarEtapa(caso: CasoDto, etapaId: string) {
    if (caso.id === 'demo-1' || caso.etapaId === etapaId) return;
    this.crm.cambiarEtapa(caso.id, etapaId).subscribe(() => this.cargar());
  }

  abrirPaciente(contactoId: string) {
    if (contactoId === 'con-1') return;
    this.router.navigate(['/pacientes', contactoId]);
  }

  abrirConversacion(casoId: string) {
    if (casoId === 'demo-1') return;
    this.router.navigate(['/conversaciones'], { queryParams: { caso: casoId } });
  }

  esUrgente(c: CasoDto): boolean {
    return c.semaforo === 'vencido' || c.semaforo === 'por_vencer';
  }

  formatearVencimiento(c: CasoDto): string {
    if (!c.fechaVencimiento) return 'En término';
    const vence = new Date(c.fechaVencimiento);
    const diffHoras = Math.round((vence.getTime() - Date.now()) / 3600000);
    if (diffHoras <= 0) return 'Prioritario hoy';
    if (diffHoras < 24) return `Vence ${diffHoras}h`;
    return 'En término';
  }

  // ===========================================================================
  // NUEVAS FUNCIONALIDADES: COTIZACIÓN PDF, EXCEL, AGENDA, AUTOMATIZACIONES Y CAMPAÑAS
  // ===========================================================================
  readonly cotizacionModalCaso = signal<CasoDto | null>(null);
  readonly notificacionSistema = signal<string>('');

  // Agenda Médica y Quirúrgica Visual
  readonly filtroEspecialistaAgenda = signal<string>('todos');
  readonly vistaCalendario = signal<'semana' | 'dia' | 'quirofanos'>('semana');
  readonly nuevaCitaDia = signal<string>('Jueves');
  readonly nuevaCitaHora = signal<string>('10:00 AM');
  readonly nuevaCitaEspecialista = signal<string>('Dr. Javier Méndez');
  readonly nuevaCitaTipo = signal<string>('Cirugía Facoemulsificación + Lente Trifocal');

  readonly citasAgendaLocal = signal<Array<{
    id: string;
    dia: string;
    hora: string;
    paciente: string;
    documento: string;
    procedimiento: string;
    especialista: string;
    sala: string;
    convenio: string;
    tono: 'blue' | 'green' | 'amber' | 'purple';
  }>>([
    { id: 'ag-1', dia: 'Lunes', hora: '08:00 AM', paciente: 'Elena Vargas', documento: 'CC 1000000015', procedimiento: 'Valoración Preanestésica + Pentacam', especialista: 'Dra. Laura Gómez', sala: 'Consultorio 2 · Diagnóstico', convenio: 'Póliza Sura', tono: 'blue' },
    { id: 'ag-2', dia: 'Lunes', hora: '10:00 AM', paciente: 'Carlos Rivas', documento: 'CC 73145890', procedimiento: 'Facoemulsificación + Lente Trifocal OD', especialista: 'Dr. Javier Méndez', sala: 'Quirófano 1 · Microscopía Zeiss', convenio: 'Póliza Sura', tono: 'green' },
    { id: 'ag-3', dia: 'Martes', hora: '09:00 AM', paciente: 'Ana Pérez', documento: 'CC 45123987', procedimiento: 'Tomografía OCT Macular + Biometría IOL', especialista: 'Dr. Carlos Restrepo', sala: 'Unidad Pentacam / OCT', convenio: 'Sanitas EPS', tono: 'purple' },
    { id: 'ag-4', dia: 'Miércoles', hora: '08:00 AM', paciente: 'Laura Mendoza', documento: 'CC 32890112', procedimiento: 'Cirugía Refractiva LASIK Bilateral', especialista: 'Dra. Laura Gómez', sala: 'Quirófano 2 · Láser Excimer', convenio: 'Particular', tono: 'green' },
    { id: 'ag-5', dia: 'Miércoles', hora: '11:00 AM', paciente: 'Jorge Torres', documento: 'CC 79451200', procedimiento: 'Revisión de Exámenes Prequirúrgicos', especialista: 'Dr. Javier Méndez', sala: 'Consultorio 1 · Catarata', convenio: 'Mutual', tono: 'amber' },
    { id: 'ag-6', dia: 'Jueves', hora: '09:00 AM', paciente: 'María Fernanda Ruiz', documento: 'CC 52891034', procedimiento: 'Implante de Anillos Intracorneales', especialista: 'Dra. Laura Gómez', sala: 'Quirófano 1 · Microscopía Zeiss', convenio: 'Póliza Sura', tono: 'green' },
    { id: 'ag-7', dia: 'Viernes', hora: '10:00 AM', paciente: 'Roberto Gómez', documento: 'CC 13890411', procedimiento: 'Inyección Intravítrea Antiangiogénica', especialista: 'Dr. Carlos Restrepo', sala: 'Quirófano 2 · Retina', convenio: 'Sanitas EPS', tono: 'blue' },
    { id: 'ag-8', dia: 'Sábado', hora: '08:00 AM', paciente: 'Lucía Gómez', documento: 'CC 60341922', procedimiento: 'Control Post-Quirúrgico 7 Días', especialista: 'Dr. Javier Méndez', sala: 'Consultorio 1 · Catarata', convenio: 'Póliza Sura', tono: 'amber' },
  ]);

  // Centro de Actividad y Seguimiento Diario (3 Pestañas Claras)
  readonly pestanaActividad = signal<'tareas' | 'automatizaciones' | 'campanas' | 'reportes' | 'bitacora'>('tareas');
  readonly tareasCompletadasMap = signal<Record<string, boolean>>({});
  readonly eventosRecientesEquipo = signal<Array<{
    id: string;
    hace: string;
    asesora: string;
    iniciales: string;
    tono: 'blue' | 'green' | 'purple' | 'amber';
    accion: string;
    detalle: string;
  }>>([]);

  grupoTareasConfirmar(): CasoDto[] {
    const lista = this.casos();
    const filtrados = lista.filter(c =>
      c.etapa.toLowerCase().includes('confirm') ||
      c.etapa.toLowerCase().includes('program') ||
      c.embudo.toLowerCase().includes('quirurg')
    );
    return (filtrados.length ? filtrados : lista).slice(0, 4);
  }

  grupoTareasAutorizaciones(): CasoDto[] {
    const lista = this.casos();
    const filtrados = lista.filter(c =>
      c.etapa.toLowerCase().includes('autoriz') ||
      c.etapa.toLowerCase().includes('gestión') ||
      c.etapa.toLowerCase().includes('orden')
    );
    return (filtrados.length ? filtrados : lista.slice(2)).slice(0, 5);
  }

  grupoTareasRedes(): CasoDto[] {
    const lista = this.casos();
    const filtrados = lista.filter(c =>
      c.origen === 'whatsapp' ||
      c.origen === 'instagram' ||
      c.origen === 'messenger' ||
      c.etapa.toLowerCase().includes('respuesta')
    );
    return (filtrados.length ? filtrados : lista.slice(1)).slice(0, 4);
  }

  tareasPendientesHoyCount(): number {
    const todos = [
      ...this.grupoTareasConfirmar(),
      ...this.grupoTareasAutorizaciones(),
      ...this.grupoTareasRedes()
    ];
    const unicos = Array.from(new Set(todos.map(x => x.id)));
    return unicos.filter(id => !this.tareasCompletadasMap()[id]).length;
  }

  estaTareaCompletada(casoId: string): boolean {
    return !!this.tareasCompletadasMap()[casoId];
  }

  marcarTareaCompletada(caso: CasoDto, categoria: string) {
    const nuevoEstado = !this.estaTareaCompletada(caso.id);
    this.tareasCompletadasMap.update(m => ({ ...m, [caso.id]: nuevoEstado }));
    if (nuevoEstado) {
      const asesora = caso.responsable || 'Dra. Laura Gómez';
      const iniciales = this.iniciales(asesora);
      this.eventosRecientesEquipo.update(actuales => [
        {
          id: `ev-${Date.now()}`,
          hace: 'Justo ahora',
          asesora,
          iniciales,
          tono: 'green',
          accion: `completó ${categoria.toLowerCase()} de ${this.limpiarNombre(caso.contacto)}`,
          detalle: `${caso.titulo} · ${this.obtenerConvenioCaso(caso)}`
        },
        ...actuales
      ]);
      if (caso.id !== 'demo-1') {
        this.crm.agregarNota(caso.id, `✓ Tarea diaria completada (${categoria}): ${caso.titulo}`).subscribe();
      }
      this.mostrarAviso(`✓ Tarea marcada como completada para ${this.limpiarNombre(caso.contacto)}`);
    }
  }

  resolverTareaAhora(caso: CasoDto) {
    if (!this.estaTareaCompletada(caso.id)) {
      this.marcarTareaCompletada(caso, 'Gestión prioritaria del día');
    }
    this.seleccionarCasoInspector(caso);
    this.irAVista('casos');
  }

  bitacoraEquipoReciente() {
    const base = this.casos().slice(0, 5).map((c, idx) => {
      const asesora = c.responsable || (idx % 2 === 0 ? 'Dra. Laura Gómez' : 'Coordinación Clínica');
      const tiempos = ['Hace 5 min', 'Hace 14 min', 'Hace 28 min', 'Hace 42 min', 'Hace 1 h'];
      const acciones = [
        `confirmó programación de ${c.titulo} para ${this.limpiarNombre(c.contacto)}`,
        `radicó orden y autorización (${this.obtenerConvenioCaso(c)}) de ${this.limpiarNombre(c.contacto)}`,
        `respondió mensaje/comentario de red social a ${this.limpiarNombre(c.contacto)}`,
        `validó exámenes prequirúrgicos OD/OI de ${this.limpiarNombre(c.contacto)}`,
        `actualizó seguimiento en etapa "${this.etapaLegible(c.etapa)}" para ${this.limpiarNombre(c.contacto)}`
      ];
      const tonos: Array<'blue' | 'green' | 'purple' | 'amber'> = ['green', 'blue', 'purple', 'amber', 'green'];
      return {
        id: `base-${c.id}-${idx}`,
        hace: tiempos[idx % tiempos.length],
        asesora,
        iniciales: this.iniciales(asesora),
        tono: tonos[idx % tonos.length],
        accion: acciones[idx % acciones.length],
        detalle: `${c.embudo} · ${this.obtenerConvenioCaso(c)}`
      };
    });
    return [...this.eventosRecientesEquipo(), ...base];
  }

  readonly reglasAutomatizacion = signal<Array<{
    id: string;
    titulo: string;
    disparador: string;
    accion: string;
    proceso: string;
    ejecuciones: number;
    activa: boolean;
  }>>([
    {
      id: 'aut-1',
      titulo: 'Envío automático de preparación prequirúrgica',
      disparador: 'Al avanzar etapa a "Programación de Cirugía"',
      accion: 'Enviar protocolo de ayuno y gotas por WhatsApp + Crear tarea 24h antes',
      proceso: 'Ordenamientos Quirurgicos',
      ejecuciones: 142,
      activa: true,
    },
    {
      id: 'aut-2',
      titulo: 'Alerta de rescate para pacientes sin respuesta (48h)',
      disparador: 'Si permanece 2 días en "No Contesta - No Responde"',
      accion: 'Etiquetar como "Prioritario" + Asignar llamada a Dra. Laura Gómez',
      proceso: 'Todos los procesos',
      ejecuciones: 89,
      activa: true,
    },
    {
      id: 'aut-3',
      titulo: 'Notificación de Orden EPS / Póliza Autorizada',
      disparador: 'Al marcar etiqueta "Autorización EPS" en Convenios',
      accion: 'Notificar al paciente por WhatsApp y habilitar cupo en Quirófano',
      proceso: 'Convenios y Pólizas',
      ejecuciones: 64,
      activa: true,
    },
    {
      id: 'aut-4',
      titulo: 'Asignación inteligente en turno (Round-Robin)',
      disparador: 'Al ingresar nuevo paciente desde Campañas o WhatsApp',
      accion: 'Distribuir equitativamente entre coordinadoras en turno activo',
      proceso: 'Campañas',
      ejecuciones: 215,
      activa: true,
    },
    {
      id: 'aut-5',
      titulo: 'Seguimiento Post-Quirúrgico Automático (24h y 7 días)',
      disparador: 'Al completar cirugía facoemulsificación o LASIK',
      accion: 'Enviar signos de alarma + Confirmar cita de control post-operatorio',
      proceso: 'Ordenamientos Quirurgicos',
      ejecuciones: 53,
      activa: false,
    },
  ]);

  nuevaReglaTitulo = '';
  nuevaReglaDisparador = 'Al avanzar etapa a "Validación de Autorización"';
  nuevaReglaAccion = 'Enviar mensaje de confirmación por WhatsApp y etiquetar caso';

  segmentoCampana: 'quirurgicos' | 'prioritarios' | 'sura' | 'todos' = 'quirurgicos';
  nombreCampana = 'Jornada Especial de Cirugía de Catarata y Lente Trifocal';
  mensajeCampana = 'Hola {{nombre}}, te saludamos del Centro Oftalmológico Ebenezer. Tenemos cupos prioritarios habilitados esta semana para avanzar con tu procedimiento "{{procedimiento}}". ¿Prefieres agendar en jornada de la mañana o de la tarde?';

  readonly historialCampanas = signal<Array<{
    id: string;
    nombre: string;
    segmento: string;
    enviados: number;
    entregadosPct: number;
    respuestas: number;
    fecha: string;
  }>>([
    { id: 'cmp-1', nombre: 'Confirmación Quirúrgica Póliza Sura y Prepagadas', segmento: 'Póliza Sura', enviados: 28, entregadosPct: 98, respuestas: 19, fecha: 'Hoy · 09:15 AM' },
    { id: 'cmp-2', nombre: 'Jornada Diagnóstica Pentacam + Biometría OD/OI', segmento: 'Diagnósticos', enviados: 34, entregadosPct: 96, respuestas: 22, fecha: 'Ayer · 04:30 PM' },
    { id: 'cmp-3', nombre: 'Recuperación de Pacientes Pendientes de Agendar', segmento: 'Ordenamientos Quirúrgicos', enviados: 45, entregadosPct: 94, respuestas: 27, fecha: '18 Oct 2026' },
  ]);

  readonly motivosDescarte = signal<Array<{
    motivo: string;
    cantidad: number;
    porcentaje: number;
    color: string;
  }>>([
    { motivo: 'En espera de autorización de EPS / Aseguradora', cantidad: 18, porcentaje: 42, color: '#186ADE' },
    { motivo: 'Presupuesto particular en evaluación familiar', cantidad: 10, porcentaje: 24, color: '#D97706' },
    { motivo: 'Teléfono apagado / No contesta tras 3 intentos', cantidad: 8, porcentaje: 19, color: '#DF6D59' },
    { motivo: 'Reprogramado por control médico general (HTA/Glicemia)', cantidad: 6, porcentaje: 15, color: '#16A34A' },
  ]);

  mostrarAviso(mensaje: string) {
    this.notificacionSistema.set(mensaje);
    setTimeout(() => {
      if (this.notificacionSistema() === mensaje) {
        this.notificacionSistema.set('');
      }
    }, 4500);
  }

  abrirCotizacionPdf(caso: CasoDto) {
    this.cotizacionModalCaso.set(caso);
  }

  cerrarCotizacionPdf() {
    this.cotizacionModalCaso.set(null);
  }

  calcularDesgloseCotizacion(caso: CasoDto) {
    const baseValor = caso.valor && caso.valor > 500000 ? caso.valor : 4800000;
    const esQuirurgico = caso.embudo.toLowerCase().includes('quirurg') || caso.titulo.toLowerCase().includes('cirug') || caso.titulo.toLowerCase().includes('faco');
    const baseQx = esQuirurgico ? Math.round(baseValor * 0.55) : Math.round(baseValor * 0.70);
    const lenteValor = esQuirurgico ? Math.round(baseValor * 0.30) : Math.round(baseValor * 0.15);
    const salaValor = baseValor - baseQx - lenteValor;
    const convenio = this.obtenerConvenioCaso(caso);
    const coberturaPct = convenio.toLowerCase().includes('particular') ? 15 : (convenio.toLowerCase().includes('sura') ? 95 : 85);
    const descuentoPoliza = Math.round(baseValor * (coberturaPct / 100));
    const copagoFinal = Math.max(0, baseValor - descuentoPoliza);
    const codigoCups = esQuirurgico ? '13.41.00 · Facoemulsificación + LIO' : '95.12.01 · Biometría & Topografía Pentacam';
    return {
      baseQx,
      lenteValor,
      salaValor,
      subtotal: baseValor,
      coberturaPct,
      descuentoPoliza,
      copagoFinal,
      codigoCups,
      convenio,
    };
  }

  imprimirCotizacionPdf() {
    window.print();
  }

  enviarCotizacionAlChat(caso: CasoDto) {
    const desglose = this.calcularDesgloseCotizacion(caso);
    const nota = `📄 Cotización / Orden enviada (${this.codigoRadicadoConvenio(caso, 0)}): ${caso.titulo} · Cobertura ${desglose.convenio} (${desglose.coberturaPct}%) · Valor copago: $${desglose.copagoFinal.toLocaleString('es-CO')}`;
    this.mensajesEnviadosInspector.update(l => [nota, ...l]);
    if (caso.id !== 'demo-1') {
      this.crm.agregarNota(caso.id, nota).subscribe();
    }
    this.cerrarCotizacionPdf();
    this.mostrarAviso(`Cotización PDF registrada y enviada al historial de ${this.limpiarNombre(caso.contacto)}`);
  }

  exportarCasosExcel() {
    const lista = this.casosFiltrados();
    const encabezados = [
      'N_Radicado',
      'Paciente',
      'Documento',
      'Telefono',
      'Procedimiento_Clinico',
      'Proceso',
      'Etapa_Actual',
      'Convenio_Aseguradora',
      'Responsable',
      'Valor_COP',
      'Estado_Tiempo'
    ];
    const filas = lista.map((c, idx) => {
      const campos = [
        this.codigoRadicadoConvenio(c, idx),
        this.limpiarNombre(c.contacto),
        c.contactoDocumento || 'CC 1000000015',
        c.contactoTelefono || '+57 300 412 8899',
        c.titulo,
        c.embudo,
        this.etapaLegible(c.etapa),
        this.obtenerConvenioCaso(c),
        c.responsable || 'Dra. Laura Gómez',
        String(c.valor || 4800000),
        this.textoCronometroTiempo(c, idx)
      ];
      return campos.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';');
    });
    const contenidoCsv = '\uFEFF' + [encabezados.join(';'), ...filas].join('\r\n');
    const blob = new Blob([contenidoCsv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const enlace = document.createElement('a');
    enlace.href = url;
    enlace.download = `NexaMed_CRM_Ebenezer_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(enlace);
    enlace.click();
    document.body.removeChild(enlace);
    URL.revokeObjectURL(url);
    this.mostrarAviso(`Archivo Excel exportado con éxito (${lista.length} casos clínicos).`);
  }

  importarCasosDesdeExcel(evento: Event) {
    const input = evento.target as HTMLInputElement;
    const archivo = input.files?.[0];
    if (!archivo) return;
    const lector = new FileReader();
    lector.onload = () => {
      const texto = String(lector.result || '');
      const lineas = texto.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      if (lineas.length <= 1) {
        this.mostrarAviso('El archivo seleccionado no contiene filas adicionales para importar.');
        return;
      }
      const embudoBase = this.embudos()[0];
      const etapaBase = embudoBase?.etapas?.[0];
      const nuevosCasos: CasoDto[] = [];
      for (let i = 1; i < Math.min(lineas.length, 25); i++) {
        const cols = lineas[i].split(/[;,]/).map(x => x.replace(/^"|"$/g, '').trim());
        const nombrePac = cols[1] || cols[0] || `Paciente Importado ${i}`;
        const docPac = cols[2] || `CC 1000099${i}`;
        const proc = cols[4] || cols[3] || 'Valoración Oftalmológica Integral';
        const conv = cols[7] || 'Póliza Sura';
        nuevosCasos.push({
          id: `imp-${Date.now()}-${i}`,
          embudoId: embudoBase?.id || 'emb-1',
          embudo: embudoBase?.nombre || 'Ordenamientos Quirurgicos',
          etapaId: etapaBase?.id || 'et-1',
          etapa: etapaBase?.nombre || 'En Valoración Inicial',
          contactoId: `con-imp-${i}`,
          contacto: nombrePac,
          contactoDocumento: docPac,
          contactoTelefono: '+57 300 555 0199',
          aseguradora: conv,
          responsableId: null,
          responsable: 'Dra. Laura Gómez',
          etiquetas: [conv, 'Importado Excel'],
          titulo: proc,
          estado: 'abierto',
          origen: 'manual',
          valor: 4200000,
          fechaVencimiento: new Date(Date.now() + 86400000).toISOString(),
          creadoEn: new Date().toISOString(),
          etapaDesde: new Date().toISOString(),
          semaforo: 'a_tiempo',
        });
      }
      if (nuevosCasos.length > 0) {
        this.casos.update(actuales => [...nuevosCasos, ...actuales]);
        this.seleccionarCasoInspector(nuevosCasos[0]);
        this.mostrarAviso(`¡Importación completada! Se cargaron ${nuevosCasos.length} casos desde "${archivo.name}".`);
      }
      input.value = '';
    };
    lector.readAsText(archivo, 'utf-8');
  }

  citasAgendaFiltradas() {
    const esp = this.filtroEspecialistaAgenda();
    const vista = this.vistaCalendario();
    return this.citasAgendaLocal().filter(c => {
      if (esp !== 'todos' && c.especialista !== esp) return false;
      if (vista === 'dia' && c.dia !== 'Lunes' && c.dia !== 'Jueves') return false;
      if (vista === 'quirofanos' && !c.sala.toLowerCase().includes('quirófano')) return false;
      return true;
    });
  }

  citasPorDia(dia: string) {
    return this.citasAgendaFiltradas().filter(c => c.dia === dia);
  }

  agendarCitaRapida() {
    const sel = this.casoActualVista();
    const nueva = {
      id: `ag-${Date.now()}`,
      dia: this.nuevaCitaDia(),
      hora: this.nuevaCitaHora(),
      paciente: this.limpiarNombre(sel.contacto),
      documento: sel.contactoDocumento || 'CC 1000000015',
      procedimiento: this.nuevaCitaTipo() || sel.titulo,
      especialista: this.nuevaCitaEspecialista(),
      sala: this.nuevaCitaEspecialista().includes('Méndez') ? 'Quirófano 1 · Microscopía Zeiss' : 'Consultorio Especializado',
      convenio: this.obtenerConvenioCaso(sel),
      tono: 'green' as const,
    };
    this.citasAgendaLocal.update(lista => [nueva, ...lista]);
    if (sel.id !== 'demo-1') {
      this.crm.agregarNota(sel.id, `📅 Cita programada: ${nueva.procedimiento} (${nueva.dia} ${nueva.hora}) con ${nueva.especialista}`).subscribe();
    }
    this.mostrarAviso(`Cita agendada para ${nueva.paciente}: ${nueva.dia} a las ${nueva.hora} con ${nueva.especialista}`);
  }

  alternarReglaAutomatizacion(id: string) {
    this.reglasAutomatizacion.update(lista =>
      lista.map(r => (r.id === id ? { ...r, activa: !r.activa } : r))
    );
  }

  crearReglaAutomatizacion() {
    const titulo = this.nuevaReglaTitulo.trim();
    if (!titulo) {
      this.mostrarAviso('Escribe un nombre descriptivo para la nueva regla automática.');
      return;
    }
    const nueva = {
      id: `aut-${Date.now()}`,
      titulo,
      disparador: this.nuevaReglaDisparador,
      accion: this.nuevaReglaAccion,
      proceso: 'Ordenamientos Quirurgicos',
      ejecuciones: 1,
      activa: true,
    };
    this.reglasAutomatizacion.update(lista => [nueva, ...lista]);
    this.nuevaReglaTitulo = '';
    this.mostrarAviso(`Regla automática "${titulo}" activada en NexaMed CRM.`);
  }

  ejecutarReglaAhora(id: string) {
    const regla = this.reglasAutomatizacion().find(r => r.id === id);
    if (!regla) return;
    this.reglasAutomatizacion.update(lista =>
      lista.map(r => (r.id === id ? { ...r, ejecuciones: r.ejecuciones + this.casosUrgentes().length || 5, activa: true } : r))
    );
    this.mostrarAviso(`Automatización ejecutada: "${regla.titulo}" aplicada sobre los casos activos.`);
  }

  pacientesSegmentoCampana(): CasoDto[] {
    const lista = this.casos();
    if (this.segmentoCampana === 'quirurgicos') {
      return lista.filter(c => c.embudo.toLowerCase().includes('quirurg') || c.titulo.toLowerCase().includes('cirug') || c.titulo.toLowerCase().includes('faco'));
    }
    if (this.segmentoCampana === 'prioritarios') {
      return this.casosUrgentes();
    }
    if (this.segmentoCampana === 'sura') {
      return lista.filter((c, i) => this.entidadConvenioDistribuida(c, i) === 'Póliza Sura');
    }
    return lista;
  }

  lanzarCampanaMasiva() {
    const destinatarios = this.pacientesSegmentoCampana();
    const total = Math.max(1, destinatarios.length);
    const etiquetaSeg =
      this.segmentoCampana === 'quirurgicos' ? 'Pacientes Quirúrgicos' :
      this.segmentoCampana === 'prioritarios' ? 'Prioritarios Hoy' :
      this.segmentoCampana === 'sura' ? 'Póliza Sura' : 'Todos los Pacientes';

    const nueva = {
      id: `cmp-${Date.now()}`,
      nombre: this.nombreCampana.trim() || 'Campaña de Seguimiento Clínico Ebenezer',
      segmento: etiquetaSeg,
      enviados: total,
      entregadosPct: 99,
      respuestas: Math.max(1, Math.round(total * 0.65)),
      fecha: 'Hace unos instantes',
    };
    this.historialCampanas.update(lista => [nueva, ...lista]);
    const muestra = destinatarios[0];
    if (muestra && muestra.id !== 'demo-1') {
      const msgPersonalizado = this.mensajeCampana
        .replace('{{nombre}}', this.limpiarNombre(muestra.contacto))
        .replace('{{procedimiento}}', muestra.titulo);
      this.crm.agregarNota(muestra.id, `📣 Campaña enviada: ${msgPersonalizado}`).subscribe();
    }
    this.mostrarAviso(`🚀 Campaña "${nueva.nombre}" enviada con éxito a ${total} pacientes del segmento ${etiquetaSeg}.`);
  }
}
